"use client";

import { normalizeGenre } from "@/lib/genres";
import { LayoutDashboard } from "lucide-react";
import Link from "next/link";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { cn } from "@/lib/utils";
import { FitNumber } from "@/components/ui/fit-number";
import { storedAudioFormats, unresolvedAudioAssetCount, isAudioWork, catalogueDate, trackDurationMs, monthBuckets, canViewStreams } from "@/lib/catalogue-analytics";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { TokenBalanceWidget } from "@/components/tokens/token-balance-widget";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Image from "next/image";
import {
  Music, Heart, ListMusic, Coins, Upload, Users, Disc3, BarChart3,
  TrendingUp, TrendingDown, Activity, Star, Clock, Zap, Keyboard, MousePointer2,
  Euro, Code2, Radio, Calendar, ArrowRight,
  Play, Pause, Download, MousePointer, BarChart2, Layers, Share2, Headphones,
  Wallet, Receipt, PiggyBank, Percent, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import {
  collection, getDocs, query, where, orderBy, limit,
  getCountFromServer, Timestamp, doc, updateDoc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { BookkeepingEntry } from "@/lib/bookkeeping/types";
import { playableTracksFor } from "@/lib/playable-tracks";
import { getAppStats, type AppStats, getMediaEventStats, type MediaEventSummary, getCreatorRevenue, getCreatorExpenses, type CreatorRevenue, type CreatorExpense } from "@/services/stats";
import type { TokenTransaction } from "@/services/token-usage";
import { getDevSessionSummary, type DevSessionSummary } from "@/services/dev-session";
import { devCostSek } from "@/lib/dev-rate";
import { DevGlance } from "@/components/dashboard/dev-glance";
import { getGuestAnalytics, initGuestAnalyticsIfMissing, getBrowserProfile, type GuestAnalyticsAggregate, type BrowserProfile, type MediaCodecSupport } from "@/services/guest-analytics";
import { useRouter } from "next/navigation";
import { PlatformGlance } from "@/components/dashboard/platform-glance";
import { summariseTracks, type TracksGlance } from "@/lib/dashboard-glance";
import {
  Globe, Monitor, Smartphone, Tablet, Cpu, HardDrive, Wifi, Battery,
  Eye, EyeOff, Info, Shield, Database, Lock, AlertCircle, CheckCircle2, XCircle,
  Clock3, Fingerprint, Server, TerminalSquare, BookOpen,
  Sun, Moon, Volume2, Film, Languages, Map, Maximize2, Gauge, RotateCcw, Sparkles,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// A release is visible unless hidden itself (bulk adminHidden or the Media
// Vault eye-toggle `hidden`) or EVERY track on it is a vault-hidden work —
// singles auto-create one-track releases, so hiding the song must hide the card.
function releaseVisibleFn(hiddenWorkIds: Set<string>) {
  return (r: any): boolean => {
    if (r.adminHidden || r.hidden === true) return false;
    const trackIds = Array.isArray(r.tracks)
      ? r.tracks.map((t: any) => t.workId || t.id).filter(Boolean)
      : [];
    if (trackIds.length > 0 && trackIds.every((id: string) => hiddenWorkIds.has(id))) return false;
    return true;
  };
}

function fmt(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

/** Whole kronor — dev time is valued at SCB's average developer salary (lib/dev-rate.ts). */
function fmtSek(n: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(Math.round(n));
}

function fmtDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Shows full mm:ss (e.g. "3m 42s") for per-track duration display */
function fmtDurationFull(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function timeAgo(ts: Timestamp | string | number | undefined | null): string {
  if (ts == null) return "";
  let millis: number;
  if (typeof (ts as any)?.toMillis === "function") {
    millis = (ts as any).toMillis();
  } else if (typeof ts === "string") {
    millis = new Date(ts).getTime();
  } else if (typeof ts === "number") {
    // handle seconds vs ms: Firestore seconds are < 2e10
    millis = ts > 2_000_000_000 ? ts : ts * 1000;
  } else {
    const date = catalogueDate(ts);
    if (!date) return "";
    millis = date.getTime();
  }
  if (!isFinite(millis)) return "";
  const ms = Date.now() - millis;
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899"];

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon, label, value, sub, accent = false, onClick, active = false
}: {
  icon: React.ElementType; label: string; value: string | number; sub?: string;
  accent?: boolean; onClick?: () => void; active?: boolean;
}) {
  return (
    <Card
      className={cn(
        "p-4 flex flex-col transition-all duration-200 [container-type:inline-size]",
        accent && "border-primary/40 bg-primary/5",
        onClick && "cursor-pointer hover:border-foreground/30 select-none",
        active && "ring-2 ring-foreground/30 border-foreground/40 bg-foreground/5"
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-expanded={onClick ? active : undefined}
      onKeyDown={onClick ? event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onClick(); } } : undefined}
    >
      {/* Icon beside the label, the number on its own row and sized to fit
          (Gordon 2026-11-03: numbers were cut off next to the icon). */}
      <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className={cn("h-3.5 w-3.5 shrink-0", accent && "text-primary")} aria-hidden="true" />
        <span className="truncate">{label}</span>
      </p>
      <FitNumber className="mt-1">{typeof value === "number" ? fmt(value) : value}</FitNumber>
      <div className="min-w-0">
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        {onClick && (
          <p className="text-[10px] text-muted-foreground/50 mt-1">{active ? "▲ click to collapse" : "▼ click for details"}</p>
        )}
      </div>
    </Card>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse bg-muted rounded-lg", className)} />;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

// Tabs a link may open with ?tab=…
const DASHBOARD_TABS = new Set(["my-hub", "platform", "transparency", "why", "angels", "join", "analytics", "finance", "visitor", "dev"]);
// Platform Transparency loads only when its tab is opened.
const PlatformTransparency = dynamic(
  () => import("@/components/dashboard/platform-transparency").then(m => m.PlatformTransparency),
  { ssr: false },
);
// Why BB (the former /why-bb page) likewise.
const WhyBB = dynamic(() => import("@/components/dashboard/why-bb").then(m => m.WhyBB), { ssr: false });
// Join BB — the former /join page (Gordon 2026-11-02: "put the join page in the dashboard panel under a JOIN BB tab").
const JoinBB = dynamic(() => import("@/components/dashboard/join-bb").then(m => m.JoinBB), { ssr: false });
// Angels (the former /supporters page) likewise.
const Angels = dynamic(() => import("@/components/dashboard/angels").then(m => m.Angels), { ssr: false });

export default function DashboardPage() {
  const { user, userProfile, loading: authLoading } = useAuth();
  const router = useRouter();
  const tCommon = useTranslations("common");
  const tGlance = useTranslations("dashboardGlance");
  const tJoin = useTranslations("join");
  const dashLocale = useLocale();
  const { playTrack, playTracks, currentTrack, isPlaying, togglePlay } = useMediaPlayer();
  const tHome = useTranslations("home");
  const { toast } = useToast();
  // Recent Releases play like Home's cards (Gordon 2026-11-01): the button
  // plays the release, or pauses/resumes it when it's the one playing.
  const [playingFrom, setPlayingFrom] = useState<{ cardId: string; trackIds: string[] } | null>(null);
  const isCardCurrent = (cardId: string) =>
    !!currentTrack && (currentTrack.id === cardId || (playingFrom?.cardId === cardId && playingFrom.trackIds.includes(currentTrack.id)));
  const playCard = async (e: React.MouseEvent, rel: any) => {
    e.stopPropagation();
    if (isCardCurrent(rel.id)) { togglePlay(); return; }
    try {
      const tracks = await playableTracksFor(rel);
      if (tracks.length === 0) { toast({ title: tHome("nothingToPlay") }); return; }
      setPlayingFrom({ cardId: rel.id, trackIds: tracks.map(tr => tr.id) });
      if (tracks.length === 1) playTrack(tracks[0]); else playTracks(tracks, 0);
    } catch {
      toast({ title: tHome("nothingToPlay") });
    }
  };

  // Playable tracks on a release: audio attached AND not hidden. Gordon: "only
  // show release tracks that have audio attached; if not, don't show."
  const playableTracks = (rel: any): any[] =>
    (Array.isArray(rel?.tracks) ? rel.tracks : []).filter((t: any) =>
      (t?.mp3Url || t?.audioUrl) && !hiddenWorkIdsRef.current.has(t?.workId || t?.id)
    );
  const playRelease = (rel: any) => {
    const first = playableTracks(rel)[0];
    if (!first) return;
    playTrack({
      id: first.workId || first.id || rel.id,
      title: first.title || rel.title,
      artist: rel.artistName || rel.artist?.name || first.artist || "Unknown",
      imageUrl: rel.coverArtUrl || first.coverArtUrl || "",
      audioUrl: first.mp3Url || first.audioUrl,
      mediaType: "audio",
    } as any);
  };

  const isAdmin = userProfile?.accountType === "admin" || userProfile?.accountType === "superadmin";
  const showStreams = canViewStreams(userProfile?.accountType);
  // Signed out, the page opens on the platform stats (Gordon 2026-10-30), not Visitor Intel.
  // A ?tab= in the link wins (/platform redirects to ?tab=transparency).
  const [activeTab, setActiveTabState] = useState("platform");
  useEffect(() => {
    if (authLoading) return;
    const asked = new URLSearchParams(window.location.search).get("tab");
    setActiveTabState(asked && DASHBOARD_TABS.has(asked) ? asked : (user ? "my-hub" : "platform"));
  }, [user?.uid, authLoading]);
  // Links inside the Dashboard switch tabs through lib/dashboard-tabs (the
  // address alone doesn't re-run the effect above).
  useEffect(() => {
    const onTab = (e: Event) => {
      const tab = (e as CustomEvent<{ tab?: string }>).detail?.tab;
      if (tab && DASHBOARD_TABS.has(tab)) { setActiveTabState(tab); window.scrollTo({ top: 0 }); }
    };
    window.addEventListener("bb-dashboard-tab", onTab);
    return () => window.removeEventListener("bb-dashboard-tab", onTab);
  }, []);
  // Keep the open tab in the address, so it can be shared and reloaded.
  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", tab);
      window.history.replaceState(window.history.state, "", url.toString());
    } catch { /* ignore */ }
  };
  const tPlatform = useTranslations("platform");
  const tNav = useTranslations("nav");

  // ── User stats ──
  const [userUploads, setUserUploads]   = useState<number | null>(null);
  const [userFavs, setUserFavs]         = useState<number | null>(null);
  const [userPlaylists, setUserPlaylists] = useState<number | null>(null);
  const [recentUploads, setRecentUploads] = useState<any[]>([]);
  const [recentFavs, setRecentFavs]     = useState<any[]>([]);
  const [avgTrackDurationSec, setAvgTrackDurationSec] = useState<number | null>(null);

  // ── Platform stats ──
  const [appStats, setAppStats]         = useState<AppStats | null>(null);
  const [contentBreakdown, setContentBreakdown] = useState<{ name: string; value: number }[]>([]);
  const [recentReleases, setRecentReleases] = useState<any[]>([]);
  // Vault-hidden work ids — loaded with the releases, reused by toggleFeatured.
  const hiddenWorkIdsRef = useRef<Set<string>>(new Set());
  const [allReleasesList, setAllReleasesList] = useState<any[]>([]);
  // Only releases with at least one playable track — never a title without an
  // audio file (Gordon 2026-10-30) — and none an admin has hidden.
  const listedReleases = allReleasesList.filter((r: any) =>
    !r.adminHidden && r.hidden !== true && playableTracks(r).length > 0);
  const [recentUsers, setRecentUsers]   = useState<any[]>([]);
  const [loadingUploads, setLoadingUploads] = useState(true);
  const [uploadHistoryError, setUploadHistoryError] = useState(false);
  const [uploadsOverTime, setUploadsOverTime] = useState<{ month: string; count: number }[]>([]);

  // ── Dev sessions (admin only) ──
  const [devSummary, setDevSummary]     = useState<DevSessionSummary | null>(null);

  // ── Analytics (streaming / downloads / clicks / page visits / tokens) ──
  const [mediaStats, setMediaStats]     = useState<MediaEventSummary | null>(null);
  const [tokenTxns, setTokenTxns]       = useState<TokenTransaction[]>([]);
  const [downloadHistory, setDownloadHistory] = useState<any[]>([]);
  const [playsOverTime, setPlaysOverTime] = useState<{ month: string; plays: number; downloads: number }[]>([]);

  const [loadingUser, setLoadingUser]   = useState(true);
  const [loadingPlatform, setLoadingPlatform] = useState(true);
  const [loadingDev, setLoadingDev]     = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingStreams, setLoadingStreams] = useState(true);
  const [loadingDownloads, setLoadingDownloads] = useState(true);
  const [streamError, setStreamError] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  // ── Finance ──
  const [financeRevenue, setFinanceRevenue] = useState<CreatorRevenue[]>([]);
  const [financeExpenses, setFinanceExpenses] = useState<CreatorExpense[]>([]);
  const [loadingFinance, setLoadingFinance] = useState(true);
  const [financeError, setFinanceError] = useState(false);

  // ── Visitor Intel ──
  const [activeUserStat, setActiveUserStat] = useState<string | null>(null);
  const [activeGuestStat, setActiveGuestStat] = useState<string | null>(null);
  const [guestAnalytics, setGuestAnalytics] = useState<GuestAnalyticsAggregate | null>(null);
  const [browserProfile, setBrowserProfile] = useState<BrowserProfile | null>(null);
  const [loadingVisitor, setLoadingVisitor] = useState(true);
  const [catalogueError, setCatalogueError] = useState(false);
  const [deepStats, setDeepStats] = useState<{
    genreBreakdown: {name:string;value:number}[];
    /** Tracks with no genre set — counted, not charted */
    noGenre?: number;
    formatBreakdown: {name:string;value:number}[];
    unresolvedFormats: number;
    avgDurationMs: number;
    tracksWithIsrc: number;
    tracksTotal: number;
    tracksWithLyrics: number;
    releasesTotal: number;
    avgTracksPerRelease: number;
  } | null>(null);
  // Explicit share, BPM/key coverage and top artists for the glance panels —
  // from the same works read as deepStats (no extra query).
  const [tracksGlance, setTracksGlance] = useState<TracksGlance | null>(null);

  // ── Load user data ──
  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      try {
        // My Hub shows every track the user is ON — owned OR credited as a
        // contributor (Gordon: "should show all tracks/releases the user is a
        // participant/contributor on"). contributorUserIds is a denormalised
        // array on works so we can query it directly, then merge with owned.
        const [ownerSnap, contribSnap, favsSnap, playlistsSnap] = await Promise.all([
          getDocs(query(collection(db, "works"), where("owner.userId", "==", user.uid))),
          getDocs(query(collection(db, "works"), where("contributorUserIds", "array-contains", user.uid))).catch(() => null),
          getCountFromServer(query(collection(db, "favorites"), where("userId", "==", user.uid))),
          getCountFromServer(query(collection(db, "playlists"), where("creatorId", "==", user.uid))),
        ]);

        // Merge + dedupe owned + contributed works. (Plain object — `Map` is the
        // lucide icon import in this file.)
        const byId: Record<string, any> = {};
        [...ownerSnap.docs, ...(contribSnap?.docs || [])].forEach(d => {
          if (!byId[d.id]) byId[d.id] = { id: d.id, ...d.data() };
        });
        const myWorks: any[] = Object.values(byId);
        const tsOf = (w: any) => w.createdAt?.seconds || w.createdAt?._seconds || 0;
        myWorks.sort((a, b) => tsOf(b) - tsOf(a));

        setUserUploads(myWorks.length);
        setUserFavs(favsSnap.data().count);
        setUserPlaylists(playlistsSnap.data().count);
        // Recent — newest 5 (any source, since contributions include imports).
        setRecentUploads(myWorks.slice(0, 5));
        const withDuration = myWorks.filter(w => w.durationMs && w.durationMs > 0);
        if (withDuration.length > 0) {
          const avgMs = withDuration.reduce((sum: number, w: any) => sum + w.durationMs, 0) / withDuration.length;
          setAvgTrackDurationSec(avgMs / 1000);
        }

        // Recent favorites — enrich thumbnails across all entity types
        try {
          const favQ = query(
            collection(db, "favorites"),
            where("userId", "==", user.uid),
            orderBy("createdAt", "desc"),
            limit(5)
          );
          const favSnap = await getDocs(favQ);
          const rawFavs = favSnap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];

          const needEnrich = rawFavs.filter(f => !f.imageUrl && f.itemId);

          if (needEnrich.length > 0) {
            const BATCH = 30;
            // Group missing IDs by itemType so we query only relevant collections
            const byType: Record<string, string[]> = {};
            needEnrich.forEach((f: any) => {
              const t = f.itemType || "track";
              if (!byType[t]) byType[t] = [];
              byType[t].push(f.itemId);
            });

            // Helper: batch-fetch docs from a collection, return id→imageUrl map
            const fetchImageMap = async (
              collName: string,
              ids: string[],
              pick: (d: Record<string, any>) => string,
            ): Promise<Record<string, string>> => {
              const map: Record<string, string> = {};
              await Promise.all(
                Array.from({ length: Math.ceil(ids.length / BATCH) }, (_, i) =>
                  ids.slice(i * BATCH, i * BATCH + BATCH)
                ).map(async (batch) => {
                  try {
                    const snap = await getDocs(query(collection(db, collName), where("__name__", "in", batch)));
                    snap.docs.forEach(d => {
                      const img = pick(d.data());
                      if (img) map[d.id] = img;
                    });
                  } catch { /* non-fatal */ }
                })
              );
              return map;
            };

            // Collect images per type in parallel — kept as ONE MAP PER TYPE
            // (never flattened together). Firestore auto-ids aren't scoped
            // per collection, so a users/{id} doc and a works/{id} doc CAN
            // share the same id string; flattening every type into one
            // shared object let a user's profile photo silently win over a
            // track's own cover art whenever ids collided (Gordon
            // 2026-09-20: "the user thumbnail overrides some of the track
            // thumbnails from a release with thumbnail"). Each favorite is
            // now only ever looked up in the map matching its OWN itemType.
            const allTypes = Object.keys(byType);
            const mapEntries = await Promise.all(allTypes.map(async (t) => {
              const ids = byType[t];
              switch (t) {
                case "track":
                  return [t, await fetchImageMap("works", ids, d => d.coverArtUrl || d.imageUrl || d.artworkUrl || "")] as const;
                case "release":
                  return [t, await fetchImageMap("releases", ids, d => d.coverArtUrl || (Array.isArray(d.images) && d.images[0]) || d.imageUrl || "")] as const;
                case "artist":
                  return [t, await fetchImageMap("artists", ids, d => d.imageUrl || d.avatarUrl || d.profileImageUrl || "")] as const;
                case "video":
                  return [t, await fetchImageMap("videos", ids, d => {
                    if (d.thumbnailUrl) return d.thumbnailUrl;
                    const yid = d.youtubeId || "";
                    return yid ? `https://img.youtube.com/vi/${yid}/hqdefault.jpg` : d.imageUrl || "";
                  })] as const;
                case "news":
                  return [t, await fetchImageMap("news", ids, d => d.featuredImage || d.imageUrl || d.thumbnailUrl || "")] as const;
                case "item":
                  return [t, await fetchImageMap("item", ids, d => (Array.isArray(d.media) && d.media[0]) || d.imageUrl || d.coverUrl || "")] as const;
                case "playlist":
                  return [t, await fetchImageMap("playlists", ids, d => d.imageUrl || d.coverUrl || "")] as const;
                case "radio":
                  return [t, await fetchImageMap("radioStations", ids, d => d.imageUrl || "")] as const;
                case "user":
                  return [t, await fetchImageMap("users", ids, d => d.profileImageUrl || d.photoURL || d.imageUrl || d.avatarUrl || "")] as const;
                default:
                  return [t, {}] as const;
              }
            }));
            const mapsByType: Record<string, Record<string, string>> = Object.fromEntries(mapEntries);

            // Release reverse-map (workId -> cover) for tracks still missing
            // after their own direct lookup — scoped to "track" only, so
            // still can't bleed into other types.
            const releaseMap: Record<string, string> = {};
            const trackIds = byType["track"] || [];
            const trackMap = mapsByType["track"] || {};
            const tracksMissingAfterDirect = trackIds.filter(id => !trackMap[id]);
            if (tracksMissingAfterDirect.length > 0) {
              try {
                const relSnap = await getDocs(collection(db, "releases"));
                relSnap.docs.forEach(d => {
                  const rel = d.data();
                  const cover = rel.coverArtUrl || (Array.isArray(rel.images) && rel.images[0]) || "";
                  if (!cover) return;
                  releaseMap[d.id] = cover;
                  if (Array.isArray(rel.tracks)) {
                    rel.tracks.forEach((t: any) => {
                      const wid = t.workId || t.id;
                      if (wid && !releaseMap[wid]) releaseMap[wid] = cover;
                    });
                  }
                });
              } catch { /* non-fatal */ }
            }

            // Apply enrichment to every favorite missing an imageUrl — ONLY
            // from the map matching its own itemType, plus the track-scoped
            // release fallback above.
            rawFavs.forEach((fav: any) => {
              if (fav.imageUrl) return;
              const t = fav.itemType || "track";
              fav.imageUrl = (mapsByType[t] || {})[fav.itemId] || (t === "track" ? releaseMap[fav.itemId] : "") || "";
            });
          }

          setRecentFavs(rawFavs);
        } catch {
          // skip
        }
      } catch (err) {
        console.error("Dashboard user data error:", err);
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [user?.uid]);

  // ── Load platform data ──
  useEffect(() => {
    if (authLoading || activeTab !== "platform") return;
    setLoadingPlatform(true);
    (async () => {
      // ── Phase 1: App stats + releases + recent users ──────────────────────────
      // Each isolated so one failure can't kill the whole dashboard.
      let releasesSnap: any = { docs: [] };
      try {
        const [stats, relSnap, usersSnap] = await Promise.all([
          getAppStats(),
          getDocs(collection(db, "releases")).catch(() =>
            getDocs(collection(db, "releases")).catch(() => ({ docs: [] as any[] }))
          ),
          getDocs(query(collection(db, "users"), orderBy("createdAt", "desc"), limit(6))).catch(() =>
            getDocs(query(collection(db, "users"), limit(6))).catch(() => ({ docs: [] as any[] }))
          ),
        ]);
        releasesSnap = relSnap;
        setAppStats(stats);

        const allReleases = releasesSnap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        const toMs = (v: any): number => {
          if (!v) return 0;
          if (typeof v.toMillis === 'function') return v.toMillis();
          if (typeof v === 'string') return new Date(v).getTime();
          if (typeof v === 'number') return v;
          return 0;
        };
        allReleases.sort((a: any, b: any) => {
          const aPinned = a.dashboardFeatured ? 1 : 0;
          const bPinned = b.dashboardFeatured ? 1 : 0;
          if (bPinned !== aPinned) return bPinned - aPinned;
          return toMs(b.releaseDate) - toMs(a.releaseDate);
        });
        // If any releases are pinned (dashboardFeatured), show only those in Recent.
        // Otherwise fall back to the 8 most recent. Hidden content stays out of
        // the greeting strip (same rule as home): a release is dropped when it
        // is hidden itself (adminHidden / eye-toggle `hidden`) OR when every
        // track on it is a work hidden in the Media Vault — singles auto-create
        // one-track releases, so vault-hiding the song must hide the card too.
        // allReleasesList keeps everything for the admin pin-management list.
        const [hiddenSnapA, hiddenSnapB] = await Promise.all([
          getDocs(query(collection(db, "works"), where("hidden", "==", true))).catch(() => ({ docs: [] as any[] })),
          getDocs(query(collection(db, "works"), where("adminHidden", "==", true))).catch(() => ({ docs: [] as any[] })),
        ]);
        const hiddenWorkIds = new Set<string>([...hiddenSnapA.docs, ...hiddenSnapB.docs].map((d: any) => d.id));
        hiddenWorkIdsRef.current = hiddenWorkIds;
        const visibleReleases = allReleases.filter(releaseVisibleFn(hiddenWorkIds));
        const featuredReleases = visibleReleases.filter((r: any) => r.dashboardFeatured);
        setRecentReleases(featuredReleases.length > 0 ? featuredReleases.slice(0, 12) : visibleReleases.slice(0, 8));
        setAllReleasesList(allReleases);
        setRecentUsers(usersSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })).slice(0, 6));
      } catch (err) {
        console.error("Dashboard platform base error:", err);
      }

      // ── Phase 2: Works-based analytics (isolated — never kills Phase 1) ───────
      try {
        setCatalogueError(false);
        const worksSnap = await getDocs(collection(db, "works"));

        // Content type breakdown
        const typeCounts: Record<string, number> = {};
        worksSnap.docs.forEach(d => {
          const rawType = String(d.data().type || "").trim();
          const t = !rawType || rawType.toLowerCase() === "work" ? "Unspecified" : rawType;
          typeCounts[t] = (typeCounts[t] || 0) + 1;
        });
        setContentBreakdown(
          Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }))
        );

        const audioDocs = worksSnap.docs.filter(d => isAudioWork(d.data()));

        // Deep catalogue DNA
        const genreMap: Record<string, number> = {};
        const fmtMap: Record<string, number> = {};
        let totalDuration = 0, durationCount = 0, isrcCount = 0, lyricsCount = 0, unresolvedFormats = 0, noGenre = 0;
        audioDocs.forEach(d => {
          const wd = d.data();
          // normalizeGenre merges spellings ("Hip Hop" / "hip-hop" → "Hip-Hop").
          const genre = normalizeGenre(wd.genres?.[0] || wd.genre || '');
          // No genre → counted under the chart, not charted as "Unknown" (it
          // was the biggest bar). The track's other stats still count.
          if (!genre || /^unknown$/i.test(genre)) noGenre++;
          else genreMap[genre] = (genreMap[genre] || 0) + 1;
          unresolvedFormats += unresolvedAudioAssetCount(wd);
          storedAudioFormats(wd).forEach(format => { fmtMap[format] = (fmtMap[format] || 0) + 1; });
          const duration = trackDurationMs(wd);
          if (duration !== null) { totalDuration += duration; durationCount++; }
          if (typeof wd.isrc === "string" && wd.isrc.trim()) isrcCount++;
          if (typeof wd.lyrics === "string" && wd.lyrics.trim()) lyricsCount++;
        });
        let totalReleaseTracks = 0;
        releasesSnap.docs.forEach((d: any) => { totalReleaseTracks += (d.data().tracks?.length || 0); });

        setDeepStats({
          genreBreakdown: Object.entries(genreMap).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, value]) => ({ name, value })),
          noGenre,
          formatBreakdown: Object.entries(fmtMap).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value })),
          unresolvedFormats,
          avgDurationMs: durationCount > 0 ? totalDuration / durationCount : 0,
          tracksWithIsrc: isrcCount,
          tracksTotal: audioDocs.length,
          tracksWithLyrics: lyricsCount,
          releasesTotal: releasesSnap.docs.length,
          avgTracksPerRelease: releasesSnap.docs.length > 0 ? totalReleaseTracks / releasesSnap.docs.length : 0,
        });
        setTracksGlance(summariseTracks(audioDocs.map(d => d.data())));
      } catch (err) {
        setCatalogueError(true);
        console.error("Dashboard catalogue analytics error:", err);
        // deepStats stays null → "No catalogue data available yet." fallback shown
      } finally {
        setLoadingPlatform(false);
      }
    })();
  }, [authLoading, activeTab, user?.uid]);

  // Database createTime is authoritative even when an old importer damaged createdAt.
  useEffect(() => {
    if (authLoading || activeTab !== 'platform') return;
    const controller = new AbortController();
    setLoadingUploads(true); setUploadHistoryError(false);
    void fetch('/api/catalogue/upload-history', { signal: controller.signal })
      .then(async response => { const result = await response.json(); if (!response.ok || !result.ok) throw new Error('Upload history unavailable'); return result; })
      .then(result => { if (!controller.signal.aborted) setUploadsOverTime(result.months); })
      .catch(() => { if (!controller.signal.aborted) setUploadHistoryError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoadingUploads(false); });
    return () => controller.abort();
  }, [authLoading, activeTab]);

  // ── Load visitor intel ──
  useEffect(() => {
    (async () => {
      try {
        const [analytics, profile] = await Promise.all([
          getGuestAnalytics(),
          getBrowserProfile(),
        ]);
        // If no aggregate doc exists yet, seed it so tracking starts working
        // immediately. initGuestAnalyticsIfMissing() only creates if missing.
        const finalAnalytics = analytics ?? (isAdmin ? await initGuestAnalyticsIfMissing() : null);
        setGuestAnalytics(finalAnalytics);
        setBrowserProfile(profile);
      } catch (err) {
        console.error("Visitor intel error:", err);
      } finally {
        setLoadingVisitor(false);
      }
    })();
  }, []);

  // ── Load dev sessions (admin only) ──
  useEffect(() => {
    if (!isAdmin) { setLoadingDev(false); return; }
    getDevSessionSummary()
      .then(setDevSummary)
      .catch(() => {})
      .finally(() => setLoadingDev(false));
  }, [isAdmin]);

  // Each analytics source loads independently; token reconciliation cannot hold streams back.
  useEffect(() => {
    if (activeTab !== 'analytics' || !user?.uid) return;
    let cancelled = false;
    setLoadingAnalytics(true); setTokenError(false);
    void (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/tokens/balance', { headers: { Authorization: `Bearer ${token}` } });
        const result = await response.json();
        if (!response.ok || !result.ok) throw new Error('Token history unavailable');
        const rows = (result.recent || []).map((entry: any) => ({
          id: entry.id, uid: user.uid, amount: entry.amount, balanceAfter: entry.balanceAfter,
          type: entry.amount < 0 ? 'spend' : entry.type === 'purchase' ? 'purchase' : entry.type === 'refund' ? 'refund' : 'bonus',
          action: entry.type === 'consume' ? entry.reason : undefined,
          description: entry.reason, createdAt: entry.createdAt,
        })) as TokenTransaction[];
        if (!cancelled) setTokenTxns(rows);
      } catch { if (!cancelled) setTokenError(true); }
      finally { if (!cancelled) setLoadingAnalytics(false); }
    })();
    setLoadingStreams(showStreams); setStreamError(false);
    if (showStreams) void getMediaEventStats(90, true)
      .then(stats => { if (!cancelled) { setMediaStats(stats); setPlaysOverTime(stats.playsOverTime); } })
      .catch(() => { if (!cancelled) setStreamError(true); })
      .finally(() => { if (!cancelled) setLoadingStreams(false); });
    else { setMediaStats(null); setPlaysOverTime([]); }
    setLoadingDownloads(isAdmin); setDownloadError(false);
    if (isAdmin) void getDocs(query(collection(db, 'downloads'), orderBy('downloadedAt', 'desc'), limit(20)))
      .then(snapshot => { if (!cancelled) setDownloadHistory(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))); })
      .catch(() => { if (!cancelled) setDownloadError(true); })
      .finally(() => { if (!cancelled) setLoadingDownloads(false); });
    else setDownloadHistory([]);
    return () => { cancelled = true; };
  }, [user?.uid, showStreams, isAdmin, activeTab]);

  // ── Load finance data ──
  useEffect(() => {
    setFinanceError(false);
    if (!user?.uid) { setLoadingFinance(false); return; }
    (async () => {
      try {
        // The old creatorRevenue/creatorExpenses collections are empty — nothing
        // in the app writes them (Gordon 2026-11-02: "why is not finance page
        // working"). The real numbers are the person's own books (Bookkeeping,
        // receipts via Snap): income → revenue, expense → expenses.
        const [rev, exp, books] = await Promise.all([
          getCreatorRevenue(user.uid).catch(() => [] as CreatorRevenue[]),
          getCreatorExpenses(user.uid).catch(() => [] as CreatorExpense[]),
          getDocs(collection(db, `userBookkeeping/${user.uid}/entries`))
            .then(s => s.docs.map(d => ({ id: d.id, ...d.data() }) as BookkeepingEntry))
            .catch(() => [] as BookkeepingEntry[]),
        ]);
        const bookRevenue = books.filter(e => e.type === "income").map(e => ({
          id: `books-${e.id}`, userId: user.uid, date: e.date, source: e.category || e.vendor || "Income",
          amount: Number(e.amount) || 0, currency: e.currency || "EUR", notes: e.description,
        }) as CreatorRevenue);
        const bookExpenses = books.filter(e => e.type === "expense").map(e => ({
          id: `books-${e.id}`, userId: user.uid, date: e.date, description: e.description || e.vendor || e.category,
          category: (e.category || "Other") as CreatorExpense["category"], amount: Number(e.amount) || 0,
          currency: e.currency || "EUR", taxDeductible: true,
        }) as CreatorExpense);
        setFinanceRevenue([...rev, ...bookRevenue]);
        setFinanceExpenses([...exp, ...bookExpenses]);
      } catch (err) {
        console.error("Finance data error:", err);
        setFinanceError(true);
      } finally {
        setLoadingFinance(false);
      }
    })();
  }, [user?.uid]);

  const displayName = userProfile?.displayName || user?.email?.split("@")[0] || "";
  const firstName   = displayName.split(" ")[0];

  // ── Toggle dashboardFeatured flag on a release ──────────────────────────────
  const toggleFeatured = useCallback(async (relId: string, current: boolean) => {
    try {
      await updateDoc(doc(db, 'releases', relId), { dashboardFeatured: !current });
      // Reflect the change locally
      const update = (list: any[]) =>
        list.map(r => r.id === relId ? { ...r, dashboardFeatured: !current } : r);
      setAllReleasesList(prev => {
        const updated = update(prev);
        // Recompute recent: pinned first, then by releaseDate
        const toMs = (v: any): number => {
          if (!v) return 0;
          if (typeof v.toMillis === 'function') return v.toMillis();
          if (typeof v === 'string') return new Date(v).getTime();
          if (typeof v === 'number') return v;
          return 0;
        };
        const sorted = [...updated].sort((a: any, b: any) => {
          const ap = a.dashboardFeatured ? 1 : 0;
          const bp = b.dashboardFeatured ? 1 : 0;
          if (bp !== ap) return bp - ap;
          return toMs(b.releaseDate) - toMs(a.releaseDate);
        });
        // Recent = only featured if any exist, otherwise 8 newest — hidden
        // releases/works excluded, mirroring the initial load above.
        const visible = sorted.filter(releaseVisibleFn(hiddenWorkIdsRef.current));
        const featured = visible.filter((r: any) => r.dashboardFeatured);
        setRecentReleases(featured.length > 0 ? featured.slice(0, 12) : visible.slice(0, 8));
        return updated;
      });
    } catch (e) {
      console.error('toggleFeatured error', e);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* ── Header ── */}
      {/* One page header everywhere (Andrés 2026-11-01): breadcrumb + the menu's icon + the page's name as the title, like Releases; slogans and greetings sit under it. */}
      <div className="sticky top-[var(--header-height)] z-30 bg-background/95 backdrop-blur-md border-b border-border px-page pt-3 pb-2">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 space-y-0.5">
            <Breadcrumbs heading size="lg" items={[{ navKey: "dashboard", icon: LayoutDashboard }]} />
            <p className="text-sm text-muted-foreground">
              {firstName ? tGlance("greetingName", { name: firstName }) : tGlance("greeting")} · {new Date().toLocaleDateString(dashLocale, { weekday: "long", month: "long", day: "numeric" })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* No Join button or avatar here (Gordon 2026-11-02: "too obvious") — Join BB is its own tab. */}
            {userProfile?.accountType && (
              <Badge variant="outline" className="capitalize hidden sm:flex">
                {userProfile.accountType}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-4 mt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 flex flex-wrap h-auto justify-start gap-1 pb-1">
            {user && <TabsTrigger value="my-hub">My Hub</TabsTrigger>}
            <TabsTrigger value="platform">Platform</TabsTrigger>
            {/* Analytics before Transparency (Gordon 2026-11-02). */}
            {user && <TabsTrigger value="analytics">Analytics</TabsTrigger>}
            {/* The former /platform page (Gordon 2026-11-01: merged into the Dashboard). */}
            <TabsTrigger value="transparency">{tPlatform("tabTitle")}</TabsTrigger>
            <TabsTrigger value="why">{tNav("whyBb")}</TabsTrigger>
            <TabsTrigger value="angels">{tNav("supporters")}</TabsTrigger>
            <TabsTrigger value="join">{tJoin("tab")}</TabsTrigger>
            {user && <TabsTrigger value="finance">Finance</TabsTrigger>}
            <TabsTrigger value="visitor">Visitor Intel</TabsTrigger>
            {isAdmin && <TabsTrigger value="dev">Dev Sessions</TabsTrigger>}
          </TabsList>

          {/* ══════════════════════════════════════════════════════════════
              MY HUB TAB
          ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="my-hub">
            <div className="space-y-6">
              {/* BB Tokens — balance, subscription status, Buy/Manage CTA */}
              <TokenBalanceWidget />
              {/* Stats grid */}
              {loadingUser ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
              ) : (
                // Token balance is shown once (TokenBalanceWidget above). The old
                // "Tokens" StatCard read a legacy source and showed 0 vs the widget's
                // 600 — Gordon: "balance is in 2 places" — so it's removed here.
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard icon={Upload} label="Tracks" value={userUploads ?? 0} sub="you're on" />
                  <StatCard icon={Heart} label="Favorites" value={userFavs ?? 0} sub="saved items" />
                  <StatCard icon={ListMusic} label="Playlists" value={userPlaylists ?? 0} sub="curated" />
                  <StatCard
                    icon={Clock}
                    label="Avg Track Length"
                    value={avgTrackDurationSec != null ? fmtDurationFull(avgTrackDurationSec) : "—"}
                    sub="across your catalogue"
                  />
                </div>
              )}

              {/* Recent Uploads */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                    <Music className="h-4 w-4 text-muted-foreground" /> Recent Tracks
                  </h2>
                  <button onClick={() => router.push("/media")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                    See all <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
                {loadingUser ? (
                  <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
                ) : recentUploads.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Music className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">No tracks yet — nothing you own or are credited on</p>
                    <button onClick={() => router.push("/media")} className="mt-3 text-xs text-primary hover:underline">
                      Upload your first track →
                    </button>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {recentUploads.map(work => (
                      <Card key={work.id} className="p-3 flex items-center gap-3 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => router.push(`/music/${work.id}`)}>
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {work.coverArtUrl || work.imageUrl ? (
                            <Image src={work.coverArtUrl || work.imageUrl} alt={work.title} width={40} height={40} className="object-cover w-full h-full" />
                          ) : (
                            <Music className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground truncate">{work.title}</p>
                          <p className="text-xs text-muted-foreground">{work.type || "Track"} · {timeAgo(work.createdAt)}</p>
                        </div>
                        {/* Play straight from the dashboard (Gordon: "missing
                            play button for recent tracks"). */}
                        {(work.mp3Url || work.audioUrl || work.hiResUrl) && (
                          <Button
                            size="icon"
                            variant="default"
                            className="h-8 w-8 rounded-full flex-shrink-0"
                            aria-label={`Play ${work.title}`}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              playTrack({
                                id: work.id,
                                title: work.title || "Untitled",
                                artist: work.artistName || (typeof work.artist === "string" ? work.artist : work.artist?.name) || "",
                                imageUrl: work.coverArtUrl || work.imageUrl || "",
                                audioUrl: work.mp3Url || work.audioUrl || "",
                                wavUrl: work.hiResUrl || work.wavUrl || undefined,
                                mediaType: "audio",
                              });
                            }}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">{work.status || "Published"}</Badge>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Favorites */}
              {recentFavs.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-3">
                    <Heart className="h-4 w-4 text-muted-foreground" /> Recently Saved
                  </h2>
                  <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
                    {recentFavs.map(fav => (
                      <button
                        type="button"
                        key={fav.id}
                        className="w-24 flex-shrink-0 cursor-pointer group text-left"
                        // Saved chats have no page link — open the room in the chat
                        // window, the same event the header favorites bar sends.
                        onClick={() => fav.itemType === "chat"
                          ? window.dispatchEvent(new CustomEvent("openChatRoom", { detail: { roomId: fav.itemId } }))
                          : fav.link && router.push(fav.link)}
                      >
                        <div className="w-24 h-24 rounded-xl bg-muted overflow-hidden mb-1.5">
                          {fav.imageUrl ? (
                            <Image src={fav.imageUrl} alt={fav.title} width={96} height={96} className="object-cover w-full h-full" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Heart className="h-6 w-6 text-muted-foreground/40" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-foreground font-medium truncate">{fav.title}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{fav.subtitle}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ══════════════════════════════════════════════════════════════
              PLATFORM TAB
          ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="angels">
            {activeTab === "angels" && <Angels embedded />}
          </TabsContent>

          <TabsContent value="why">
            {activeTab === "why" && <WhyBB embedded />}
          </TabsContent>

          <TabsContent value="join">
            {activeTab === "join" && <JoinBB embedded />}
          </TabsContent>

          <TabsContent value="transparency">
            {activeTab === "transparency" && <PlatformTransparency embedded />}
          </TabsContent>

          <TabsContent value="platform">
            <div className="space-y-8">

              {/* ── 1. PRIMARY KPI OVERVIEW — each card slides open its own in-depth panel ── */}
              <section>
                <h2 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <Activity className="h-3 w-3" aria-hidden="true" /> {tGlance("title")}
                </h2>
                <PlatformGlance
                  loading={loadingPlatform}
                  appStats={appStats}
                  deepStats={deepStats}
                  catalogueError={catalogueError}
                  tracks={tracksGlance}
                  releases={allReleasesList}
                  hiddenWorkIds={hiddenWorkIdsRef.current}
                  uploadsOverTime={uploadsOverTime}
                  loadingUploads={loadingUploads}
                  uploadHistoryError={uploadHistoryError}
                  guestAnalytics={guestAnalytics}
                  loadingVisitor={loadingVisitor}
                  signedIn={!!user}
                />
              </section>

              {/* ── 2. CATALOGUE INTELLIGENCE ── */}
              <section>
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
                  <Database className="h-4 w-4 text-emerald-400" />
                  CATALOGUE INTELLIGENCE
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">— x-ray of the music library</span>
                </h2>
                <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                  Metadata quality is how far music travels. ISRC codes unlock performance royalties from collecting societies worldwide.
                  Lyrics boost streaming platform algorithmic discovery. Format diversity signals a professional, distribution-ready catalogue.
                </p>

                {loadingPlatform ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                  </div>
                ) : catalogueError ? <Card role="alert" className="p-6">{tCommon("error")}</Card> : deepStats ? (
                  <>
                    {/* KPI strip */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <StatCard
                        icon={Music}
                        label="Avg Track Duration"
                        value={deepStats.avgDurationMs > 0 ? fmtDurationFull(deepStats.avgDurationMs / 1000) : "—"}
                        sub="across catalogue"
                      />
                      <StatCard
                        icon={Radio}
                        label="ISRC Coverage"
                        value={deepStats.tracksTotal > 0 ? `${((deepStats.tracksWithIsrc / deepStats.tracksTotal) * 100).toFixed(0)}%` : "—"}
                        sub={`${deepStats.tracksWithIsrc} of ${deepStats.tracksTotal} tracks`}
                        accent={deepStats.tracksTotal > 0 && deepStats.tracksWithIsrc / deepStats.tracksTotal > 0.7}
                      />
                      <StatCard
                        icon={BookOpen}
                        label="Lyrics Coverage"
                        value={deepStats.tracksTotal > 0 ? `${((deepStats.tracksWithLyrics / deepStats.tracksTotal) * 100).toFixed(0)}%` : "—"}
                        sub={`${deepStats.tracksWithLyrics} tracks with lyrics`}
                      />
                      <StatCard
                        icon={Disc3}
                        label="Avg Tracks / Release"
                        value={deepStats.releasesTotal > 0 ? deepStats.avgTracksPerRelease.toFixed(1) : "—"}
                        sub={`${deepStats.releasesTotal} releases total`}
                      />
                    </div>

                    {/* Educational context cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                      <Card className="p-3 border-emerald-500/20 bg-emerald-500/5">
                        <div className="flex items-start gap-2">
                          <Radio className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-foreground">Why ISRC Matters</p>
                            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                              ISRC (International Standard Recording Code) is the barcode of music. Every stream on Spotify, Apple Music, or broadcast on radio — the ISRC is how collecting societies like STIM, PRS, and ASCAP identify the recording and route royalties back to rights holders. No ISRC = invisible to the royalty pipeline.
                            </p>
                          </div>
                        </div>
                      </Card>
                      <Card className="p-3 border-blue-500/20 bg-blue-500/5">
                        <div className="flex items-start gap-2">
                          <BookOpen className="h-3.5 w-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-foreground">Lyrics = Discovery</p>
                            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                              Streaming platforms index lyrics for search — Spotify's engine surfaces tracks matching lyric fragments. Timestamped LRC lyrics drive playlist placement via mood and activity matching. Tracks with synced lyrics typically see 3–5× more algorithmic surface area across editorial and auto-playlisting systems.
                            </p>
                          </div>
                        </div>
                      </Card>
                      <Card className="p-3 border-amber-500/20 bg-amber-500/5">
                        <div className="flex items-start gap-2">
                          <Music className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-foreground">Format Intelligence</p>
                            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                              WAV and AIFF are lossless master formats — archive everything here. MP3 320kbps is the universal delivery format. FLAC is lossless-compressed, ideal for high-res distribution. A healthy library keeps masters in WAV/AIFF and publishes in MP3/AAC. AIF-heavy means you have high-quality masters waiting to be converted and distributed.
                            </p>
                          </div>
                        </div>
                      </Card>
                    </div>

                    {/* Genre + Format charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {deepStats.genreBreakdown.length > 0 && (
                        <Card className="p-4">
                          <h4 className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Genre Distribution</h4>
                          <p className="text-[11px] text-muted-foreground mb-3">Which genres dominate the catalogue — useful for curating genre-targeted playlists and identifying underserved scenes.{deepStats.noGenre ? ` ${deepStats.noGenre} tracks have no genre yet and aren't charted.` : ""}</p>
                          {/* Every bar labelled: interval={0} stops the axis from skipping
                              every other genre name (Gordon 2026-10-30), with room for them. */}
                          <ResponsiveContainer width="100%" height={Math.max(220, deepStats.genreBreakdown.length * 30)}>
                            <BarChart data={deepStats.genreBreakdown} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                              <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                              <YAxis dataKey="name" type="category" interval={0} tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} width={120} />
                              <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                              <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Tracks">
                                {deepStats.genreBreakdown.map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Card>
                      )}
                      {deepStats.unresolvedFormats > 0 && <p role="status" className="text-xs text-amber-600 dark:text-amber-400">{deepStats.unresolvedFormats} audio files need format metadata and are excluded from the format mix.</p>}
                      {deepStats.formatBreakdown.length > 0 && (
                        <Card className="p-4">
                          <h4 className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Audio Format Mix</h4>
                          <p className="text-[11px] text-muted-foreground mb-3">Stored audio formats across all users. Each track counts once per stored format, including original masters and delivery copies. A track can have both WAV and MP3/M4A.</p>
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie
                                data={deepStats.formatBreakdown}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={75}
                                label={({ name, percent }) => `${name} ${percent > 0 && percent < 0.01 ? "<1" : (percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                {deepStats.formatBreakdown.map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </Card>
                      )}
                    </div>
                  </>
                ) : (
                  <Card className="p-6 text-center text-muted-foreground text-sm">No catalogue data available yet.</Card>
                )}
              </section>

              {/* ── 3. UPLOAD VELOCITY & CONTENT MIX ── */}
              <section>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <TrendingUp className="h-3 w-3" /> Upload Velocity &amp; Content Mix
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="p-4">
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" /> Uploads — Last 6 Months
                    </h3>
                    <p className="text-[11px] text-muted-foreground mb-3">New catalogue items from all users, including music, videos and images. Counted by their database creation date (UTC); each current item counts once.</p>
                    {loadingUploads ? <Skeleton className="h-48" /> : uploadHistoryError ? <p role="alert">{tCommon("error")}</p> : (
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={uploadsOverTime} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                          <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                          <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} cursor={{ fill: "hsl(var(--muted)/0.3)" }} />
                          <Bar dataKey="count" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} name="Uploads" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                  <Card className="p-4">
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-muted-foreground" /> Content by Type
                    </h3>
                    <p className="text-[11px] text-muted-foreground mb-3">Types assigned to catalogue items across all users. “Work” is the generic/default item type, shown here as “Unspecified” when no more specific type (such as Song or Beat) was selected. This chart counts items, not release packages.</p>
                    {loadingPlatform ? <Skeleton className="h-48" /> : contentBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={contentBreakdown} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={100} />
                          <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Count">
                            {contentBreakdown.map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">No tracks yet</div>
                    )}
                  </Card>
                </div>
              </section>

              {/* ── 4. NEW MEMBERS (admin only) ── */}
              {isAdmin && (
                <section>
                  <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4 text-muted-foreground" /> New Members
                  </h2>
                  {loadingPlatform ? (
                    <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {recentUsers.map(u => (
                        <Card key={u.id} className="p-3 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {u.profileImageUrl ? (
                              <Image src={u.profileImageUrl} alt={u.displayName || "User"} width={32} height={32} className="object-cover w-full h-full" />
                            ) : (
                              <span className="text-xs font-bold">{(u.displayName || u.email || "?").charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{u.displayName || u.email}</p>
                            <p className="text-[10px] text-muted-foreground">{u.accountType || "consumer"} · {timeAgo(u.createdAt)}</p>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* ── 5. RECENT RELEASES ── */}
              <section>
                <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-1">
                  <Disc3 className="h-4 w-4 text-muted-foreground" /> Recent Releases
                  {allReleasesList.some((r: any) => r.dashboardFeatured) && (
                    <Badge className="bg-amber-500 text-black text-[9px] ml-1">Pinned only</Badge>
                  )}
                </h2>
                <p className="text-[10px] text-muted-foreground mb-3">
                  {allReleasesList.some((r: any) => r.dashboardFeatured)
                    ? "Showing only ★ pinned releases. Pin/unpin in All Releases below."
                    : "Showing 8 most recent. Pin releases below to choose exactly which appear here."}
                </p>
                {loadingPlatform ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-36" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {recentReleases.map(rel => (
                      <div key={rel.id} className="cursor-pointer group" onClick={() => router.push(`/music/${rel.id}`)}>
                        <div className="relative aspect-square rounded-xl bg-muted overflow-hidden mb-2">
                          {rel.coverArtUrl ? (
                            <Image src={rel.coverArtUrl} alt={rel.title} width={200} height={200} className="object-cover w-full h-full group-hover:scale-105 transition-transform" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Disc3 className="h-8 w-8 text-muted-foreground/30" />
                            </div>
                          )}
                          {rel.dashboardFeatured && (
                            <span className="absolute top-1 left-1 bg-amber-500 text-black text-[9px] font-bold px-1 py-0.5 rounded-md">★</span>
                          )}
                          {/* Same play button as Home's cards. */}
                          {(() => {
                            const playing = isCardCurrent(rel.id) && isPlaying;
                            const label = `${playing ? tCommon("pause") : tCommon("play")} ${rel.title || ""}`.trim();
                            return (
                              <Button
                                variant="ghost" size="icon"
                                className="bb-frost-play absolute bottom-2 right-2 z-[1] h-[26px] w-[26px] rounded-full text-white ring-1 ring-white/70 transition-colors hover:bg-white hover:text-black hover:ring-black/60"
                                aria-label={label} title={label} aria-pressed={playing}
                                onClick={(e) => void playCard(e, rel)}
                              >
                                {playing ? <Pause className="h-3.5 w-3.5 fill-current" /> : <Play className="h-3.5 w-3.5 fill-current" />}
                              </Button>
                            );
                          })()}
                        </div>
                        <p className="text-xs font-semibold text-foreground truncate">{rel.title}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{rel.artistName || rel.artist?.name || "Unknown"}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* ── 6. ALL RELEASES ── */}
              <section>
                <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-1">
                  <Disc3 className="h-4 w-4 text-muted-foreground" /> All Releases
                  {!loadingPlatform && listedReleases.length > 0 && (
                    <Badge variant="outline" className="ml-1 text-[10px]">{listedReleases.length}</Badge>
                  )}
                </h2>
                {isAdmin && (
                  <p className="text-[10px] text-muted-foreground mb-3">
                    Tap ★ to pin a release to &ldquo;Recent Releases&rdquo; above. When any release is pinned, only pinned ones show.
                  </p>
                )}
                {loadingPlatform ? (
                  <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
                ) : listedReleases.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Disc3 className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">No releases yet</p>
                  </Card>
                ) : (
                  <div className="space-y-1.5">
                    {listedReleases.map(rel => (
                      <Card key={rel.id} className={cn("p-3 flex items-center gap-3 transition-colors", rel.dashboardFeatured ? "border-amber-500/40 bg-amber-500/5" : "hover:bg-muted/30")}>
                        {/* Thumbnail — click to navigate */}
                        <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden flex-shrink-0 cursor-pointer" onClick={() => router.push(`/music/${rel.id}`)}>
                          {rel.coverArtUrl ? (
                            <Image src={rel.coverArtUrl} alt={rel.title} width={40} height={40} className="object-cover w-full h-full" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Disc3 className="h-4 w-4 text-muted-foreground/30" />
                            </div>
                          )}
                        </div>
                        {/* Info — click to navigate */}
                        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => router.push(`/music/${rel.id}`)}>
                          <p className="text-sm font-medium text-foreground truncate">{rel.title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {rel.artistName || rel.artist?.name || "Unknown"} · {rel.releaseType || "Release"}
                            {rel.releaseDate ? ` · ${new Date(typeof rel.releaseDate.toDate === 'function' ? rel.releaseDate.toDate() : rel.releaseDate).getFullYear()}` : ""}
                          </p>
                        </div>
                        {/* Controls */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(() => {
                            const n = playableTracks(rel).length;
                            return (
                              <>
                                {n > 0 && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); playRelease(rel); }}
                                    title="Play"
                                    aria-label={`Play ${rel.title}`}
                                    className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0"
                                  >
                                    <Play className="h-4 w-4 ml-0.5" />
                                  </button>
                                )}
                                {n > 0 && <span className="text-[10px] text-muted-foreground hidden sm:inline">{n} track{n !== 1 ? "s" : ""}</span>}
                              </>
                            );
                          })()}
                          {/* Pin button — admins only */}
                          {isAdmin && <button
                            onClick={() => toggleFeatured(rel.id, !!rel.dashboardFeatured)}
                            title={rel.dashboardFeatured ? "Remove from Recent Releases" : "Pin to Recent Releases"}
                            className={cn(
                              "flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-semibold transition-all",
                              rel.dashboardFeatured
                                ? "bg-amber-500 border-amber-500 text-black hover:bg-amber-400"
                                : "bg-transparent border-border/50 text-muted-foreground hover:border-amber-500/60 hover:text-amber-500"
                            )}
                          >
                            ★ {rel.dashboardFeatured ? "Pinned" : "Pin"}
                          </button>}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </section>

            </div>
          </TabsContent>

          {/* ══════════════════════════════════════════════════════════════
              ANALYTICS TAB  — streaming / downloads / clicks / tokens
          ══════════════════════════════════════════════════════════════ */}
          {user && (
            <TabsContent value="analytics">
              <div className="space-y-8">

                {showStreams && <>
                  {streamError && <Card role="alert" className="p-4">{tCommon("error")}</Card>}
                {/* ── ENGAGEMENT OVERVIEW ── */}
                <section>
                  <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
                    <Headphones className="h-4 w-4 text-primary" /> Engagement Overview
                    <span className="text-xs font-normal text-muted-foreground">— last 90 days</span>
                  </h2>
                  {loadingStreams ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                    </div>
                  ) : mediaStats ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      <StatCard icon={Play}          label="Total Streams"    value={mediaStats.totalPlays}     sub={`${mediaStats.playsLast7Days} last 7d`}  accent />
                      <StatCard icon={Download}      label="Downloads"        value={mediaStats.totalDownloads} sub="completed" />
                      <StatCard icon={Eye}           label="Page Views"       value={mediaStats.totalViews}     sub="track/artist pages" />
                      <StatCard icon={MousePointer}  label="Clicks"           value={mediaStats.totalClicks}    sub="link & card taps" />
                      <StatCard icon={Share2}        label="Shares"           value={mediaStats.totalShares}    sub="QR + link copies" />
                      <StatCard icon={Heart}         label="Favorites"        value={mediaStats.totalFavorites} sub="saves" />
                    </div>
                  ) : (
                    <Card className="p-8 text-center text-muted-foreground text-sm">
                      <BarChart2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      No engagement data yet — metrics accumulate as users interact with content.
                    </Card>
                  )}
                </section>

                {/* ── STREAMING & DOWNLOADS TREND ── */}
                <section>
                  <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
                    <TrendingUp className="h-4 w-4 text-emerald-400" /> Streams &amp; Downloads — Last 6 Months
                  </h2>
                  {loadingStreams ? (
                    <Skeleton className="h-56" />
                  ) : playsOverTime.length > 0 && playsOverTime.some(m => m.plays > 0 || m.downloads > 0) ? (
                    <Card className="p-4">
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={playsOverTime} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                          <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                            cursor={{ fill: "hsl(var(--muted)/0.3)" }}
                          />
                          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                          <Bar dataKey="plays"     fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} name="Streams" />
                          <Bar dataKey="downloads" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} name="Downloads" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  ) : (
                    <Card className="p-6 text-center text-muted-foreground text-sm">
                      No streaming or download events recorded yet.
                    </Card>
                  )}
                </section>

                {/* ── TOP TRACKS & TOP ARTISTS ── */}
                {mediaStats && (mediaStats.topTracks.length > 0 || mediaStats.topArtists.length > 0 || mediaStats.topDemos.length > 0) && (
                  <section>
                    <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
                      <Play className="h-4 w-4 text-indigo-400" /> Most Streamed
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Top tracks */}
                      {mediaStats.topTracks.length > 0 && (
                        <Card className="p-4">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                            <Music className="h-3.5 w-3.5" /> Top Tracks by Streams
                          </h3>
                          <div className="space-y-2">
                            {mediaStats.topTracks.slice(0, 8).map((track, i) => {
                              const maxPlays = mediaStats.topTracks[0]?.plays || 1;
                              return (
                                <div key={track.entityId} role="button" tabIndex={0}
                                  onClick={() => track.entityId && router.push(`/music/${track.entityId}`)}
                                  onKeyDown={e => { if (e.key === 'Enter' && track.entityId) router.push(`/music/${track.entityId}`); }}
                                  className="cursor-pointer rounded-md -mx-1 px-1 py-0.5 hover:bg-secondary/50 transition-colors">
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-[10px] text-muted-foreground w-4 text-right flex-shrink-0">{i + 1}</span>
                                      <div className="min-w-0">
                                        <p className="text-foreground font-medium truncate group-hover:underline">{track.entityTitle}</p>
                                        <p className="text-muted-foreground truncate">{track.entityArtist}</p>
                                      </div>
                                    </div>
                                    <span className="text-muted-foreground flex-shrink-0 ml-2 font-semibold">{track.plays.toLocaleString()}</span>
                                  </div>
                                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${(track.plays / maxPlays) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      )}

                      {/* Top artists */}
                      {mediaStats.topArtists.length > 0 && (
                        <Card className="p-4">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5" /> Top Artists by Streams
                          </h3>
                          <div className="space-y-2">
                            {mediaStats.topArtists.slice(0, 8).map((artist, i) => {
                              const maxPlays = mediaStats.topArtists[0]?.plays || 1;
                              return (
                                <div key={artist.entityId}>
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-[10px] text-muted-foreground w-4 text-right flex-shrink-0">{i + 1}</span>
                                      <p className="text-foreground font-medium truncate">{artist.entityTitle}</p>
                                    </div>
                                    <span className="text-muted-foreground flex-shrink-0 ml-2 font-semibold">{artist.plays.toLocaleString()}</span>
                                  </div>
                                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${(artist.plays / maxPlays) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      )}

                      {mediaStats.topDemos.length > 0 && (
                        <Card className="p-4">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                            <Music className="h-3.5 w-3.5" /> Top Demos &amp; Beats by Streams
                          </h3>
                          <div className="space-y-2">
                            {mediaStats.topDemos.slice(0, 8).map((demo, i) => {
                              const maxPlays = mediaStats.topDemos[0]?.plays || 1;
                              return (
                                <div key={demo.entityId} role="button" tabIndex={0}
                                  onClick={() => demo.entityId && router.push(`/music/${demo.entityId}`)}
                                  onKeyDown={e => { if (e.key === 'Enter' && demo.entityId) router.push(`/music/${demo.entityId}`); }}
                                  className="cursor-pointer rounded-md -mx-1 px-1 py-0.5 hover:bg-secondary/50 transition-colors">
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-[10px] text-muted-foreground w-4 text-right flex-shrink-0">{i + 1}</span>
                                      <p className="text-foreground font-medium truncate">{demo.entityTitle}<span className="text-muted-foreground"> · {demo.entityArtist}</span></p>
                                    </div>
                                    <span className="text-muted-foreground flex-shrink-0 ml-2 font-semibold">{demo.plays.toLocaleString()}</span>
                                  </div>
                                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${(demo.plays / maxPlays) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      )}
                    </div>
                  </section>
                )}

                {/* ── TOP GENRES ── */}
                {mediaStats && mediaStats.topGenres.length > 0 && (
                  <section>
                    <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
                      <Layers className="h-4 w-4 text-amber-400" /> Streams by Genre
                    </h2>
                    <Card className="p-4">
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={mediaStats.topGenres.slice(0, 10)} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                          <YAxis dataKey="genre" type="category" tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} width={96} interval={0} />
                          <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                          <Bar dataKey="plays" radius={[0, 4, 4, 0]} name="Streams">
                            {mediaStats.topGenres.slice(0, 10).map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </section>
                )}

                </>}

                {isAdmin && <>
                  {downloadError && <Card role="alert" className="p-4">{tCommon("error")}</Card>}
                {/* ── DOWNLOAD HISTORY ── */}
                <section>
                  <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
                    <Download className="h-4 w-4 text-blue-400" /> Download History
                  </h2>
                  {loadingDownloads ? (
                    <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
                  ) : downloadHistory.length > 0 ? (
                    <div className="space-y-2">
                      {downloadHistory.map(dl => (
                        <Card key={dl.id} className="p-3 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                            <Download className="h-4 w-4 text-blue-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{dl.trackTitle || dl.itemTitle || dl.workId || "Track"}</p>
                            <p className="text-xs text-muted-foreground">
                              {dl.format && <span className="uppercase">{dl.format}</span>}
                              {dl.format && " · "}
                              {dl.amountPaid != null ? (dl.amountPaid === 0 ? "Free" : new Intl.NumberFormat(undefined, { style: "currency", currency: (dl.currency || "EUR").toUpperCase() }).format(dl.amountPaid / 100)) : ""}
                              {" · "}
                              {(dl.downloadedAt || dl.purchasedAt)?.toDate?.()
                                ? (dl.downloadedAt || dl.purchasedAt).toDate().toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })
                                : timeAgo(dl.downloadedAt || dl.purchasedAt)}
                            </p>
                          </div>
                          {dl.amountPaid > 0 && (
                            <Badge className="bg-emerald-600 text-white text-[10px]">Paid</Badge>
                          )}
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="p-8 text-center">
                      <Download className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                      <p className="text-sm text-muted-foreground">No downloads recorded yet.</p>
                      <p className="text-xs text-muted-foreground mt-1">Downloads are logged when users grab tracks from your releases.</p>
                    </Card>
                  )}
                </section>

                </>}

                {/* ── TOKEN USAGE ── */}
                <section>
                  <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
                    <Coins className="h-4 w-4 text-amber-400" /> Token Usage
                  </h2>
                  <TokenBalanceWidget className="mb-4" />

                  {tokenError && <Card role="alert" className="p-4">{tCommon("error")}</Card>}
                  {/* Action breakdown */}
                  {tokenTxns.length > 0 && (() => {
                    const actionMap: Record<string, { label: string; count: number; tokens: number }> = {};
                    tokenTxns.filter(t => t.type === 'spend' && t.action).forEach(t => {
                      const k = t.action!;
                      if (!actionMap[k]) actionMap[k] = { label: t.description || k, count: 0, tokens: 0 };
                      actionMap[k].count++;
                      actionMap[k].tokens += Math.abs(t.amount);
                    });
                    const entries = Object.entries(actionMap).sort((a, b) => b[1].tokens - a[1].tokens);
                    if (entries.length === 0) return null;
                    const maxTokens = entries[0][1].tokens;
                    return (
                      <Card className="p-4 mb-4">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Tokens by AI Feature</h3>
                        <div className="space-y-2">
                          {entries.map(([key, data], i) => (
                            <div key={key}>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-foreground font-medium">{data.label}</span>
                                <span className="text-muted-foreground">{data.tokens} tokens · {data.count}×</span>
                              </div>
                              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${(data.tokens / maxTokens) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })()}

                  {/* Recent token transactions */}
                  {loadingAnalytics ? (
                    <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
                  ) : tokenTxns.length > 0 ? (
                    <div className="space-y-1.5">
                      {tokenTxns.map(txn => {
                        const isSpend  = txn.type === 'spend';
                        const isBonus  = txn.type === 'bonus';
                        const isPurchase = txn.type === 'purchase';
                        return (
                          <Card key={txn.id} className="px-3 py-2.5 flex items-center gap-3">
                            <div className={cn(
                              "w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold",
                              isSpend ? "bg-rose-500/10 text-rose-500" :
                              isBonus ? "bg-emerald-500/10 text-emerald-500" :
                              "bg-blue-500/10 text-blue-500"
                            )}>
                              {isSpend ? <Zap className="h-3.5 w-3.5" /> : isBonus ? <Star className="h-3.5 w-3.5" /> : <Coins className="h-3.5 w-3.5" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-foreground truncate">{txn.description}</p>
                              <p className="text-[10px] text-muted-foreground">{timeAgo(txn.createdAt)}</p>
                            </div>
                            <span className={cn("text-sm font-bold flex-shrink-0", isSpend ? "text-rose-500" : "text-emerald-500")}>
                              {isSpend ? "" : "+"}{txn.amount}
                            </span>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <Card className="p-8 text-center">
                      <Coins className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                      <p className="text-sm text-muted-foreground">No token transactions yet.</p>
                    </Card>
                  )}
                </section>

              </div>
            </TabsContent>
          )}

          {/* ══════════════════════════════════════════════════════════════
              VISITOR INTEL TAB
          ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="visitor">
            <div className="space-y-6">

              {/* ── TOP BANNER ── */}
              <div className="rounded-2xl bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border border-indigo-500/20 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <Fingerprint className="h-5 w-5 text-indigo-400" />
                  </div>
                  <div>
                    <h2 className="font-bold text-foreground text-base">Visitor Intelligence Center</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Real-time browser forensics &amp; collective visitor behaviour — no cookies, no PII, pure aggregate signal.
                      Every number here is a shadow of someone who wandered in.
                    </p>
                  </div>
                </div>
              </div>

              {/* ── YOUR BROWSER DOSSIER ── */}
              <div>
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                  <TerminalSquare className="h-4 w-4 text-indigo-400" />
                  YOUR BROWSER DOSSIER
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">— extracted the moment you loaded this page</span>
                </h3>

                {loadingVisitor ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-20" />)}
                  </div>
                ) : browserProfile ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {/* Browser & OS */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Browser</p>
                        <p className="text-lg font-bold text-foreground">{browserProfile.browser}</p>
                        <p className="text-xs text-muted-foreground">{browserProfile.os}</p>
                      </Card>

                      {/* Screen */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Screen</p>
                        <p className="text-lg font-bold text-foreground">{browserProfile.screenW}×{browserProfile.screenH}</p>
                        <p className="text-xs text-muted-foreground">{browserProfile.pixelRatio}× DPR · {browserProfile.colorDepth}-bit color</p>
                      </Card>

                      {/* Hardware */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Cpu className="h-3 w-3" />CPU Cores</p>
                        <p className="text-lg font-bold text-foreground">{browserProfile.hardwareConcurrency}</p>
                        <p className="text-xs text-muted-foreground">
                          {browserProfile.deviceMemoryGb != null ? `${browserProfile.deviceMemoryGb} GB RAM` : "RAM: hidden"}
                        </p>
                      </Card>

                      {/* Network */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Wifi className="h-3 w-3" />Network</p>
                        <p className="text-lg font-bold text-foreground">{browserProfile.connectionType ?? "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">
                          {browserProfile.downlinkMbps != null ? `~${browserProfile.downlinkMbps} Mbps` : "speed hidden"}
                          {" · "}{browserProfile.onLine ? "online" : "offline"}
                        </p>
                      </Card>

                      {/* Battery */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Battery className="h-3 w-3" />Battery</p>
                        {browserProfile.batteryLevel != null ? (
                          <>
                            <p className="text-lg font-bold text-foreground">{browserProfile.batteryLevel}%</p>
                            <p className="text-xs text-muted-foreground">{browserProfile.batteryCharging ? "⚡ Charging" : "🔋 On battery"}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-lg font-bold text-foreground">—</p>
                            <p className="text-xs text-muted-foreground">API not exposed</p>
                          </>
                        )}
                      </Card>

                      {/* Timezone & Locale */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Globe className="h-3 w-3" />Locale</p>
                        <p className="text-sm font-bold text-foreground leading-tight">{browserProfile.timezone}</p>
                        <p className="text-xs text-muted-foreground">{browserProfile.language} · {browserProfile.languages.length} lang{browserProfile.languages.length !== 1 ? 's' : ''}</p>
                      </Card>

                      {/* Touch / input */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Input</p>
                        <p className="text-lg font-bold text-foreground">
                          {browserProfile.touchPoints > 0 ? `${browserProfile.touchPoints} touch pts` : "Mouse/kbd"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {browserProfile.device} · {browserProfile.plugins} plugins
                        </p>
                      </Card>

                      {/* WebGL */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">GPU (WebGL)</p>
                        <p className="text-xs font-bold text-foreground leading-snug line-clamp-2">
                          {browserProfile.webGLRenderer ?? "Renderer hidden"}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">{browserProfile.webGLVendor ?? ""}</p>
                      </Card>

                      {/* Browser version */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Globe className="h-3 w-3" />Version</p>
                        <p className="text-sm font-bold text-foreground leading-tight">{browserProfile.browserVersion}</p>
                        <p className="text-xs text-muted-foreground">{browserProfile.osVersion}</p>
                      </Card>

                      {/* Viewport */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Maximize2 className="h-3 w-3" />Viewport</p>
                        <p className="text-lg font-bold text-foreground">{browserProfile.viewportW}×{browserProfile.viewportH}</p>
                        <p className="text-xs text-muted-foreground">window inner · {((browserProfile.viewportW / browserProfile.screenW) * 100).toFixed(0)}% of screen</p>
                      </Card>

                      {/* Color scheme + motion */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                          {browserProfile.colorScheme === 'dark' ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />} Display Prefs
                        </p>
                        <p className="text-sm font-bold text-foreground capitalize">{browserProfile.colorScheme}</p>
                        <p className="text-xs text-muted-foreground">
                          {browserProfile.reducedMotion ? '⚠ reduced motion' : 'full motion ok'}
                          {browserProfile.highContrast ? ' · forced colors' : ''}
                        </p>
                      </Card>

                      {/* Network RTT */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Gauge className="h-3 w-3" />Latency</p>
                        <p className="text-lg font-bold text-foreground">
                          {browserProfile.rttMs != null ? `${browserProfile.rttMs}ms` : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {browserProfile.connectionType ?? 'unknown'} RTT
                        </p>
                      </Card>

                      {/* Orientation */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><RotateCcw className="h-3 w-3" />Orientation</p>
                        <p className="text-sm font-bold text-foreground">
                          {browserProfile.orientation.includes('portrait') ? '↕ Portrait' : '↔ Landscape'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{browserProfile.orientation}</p>
                      </Card>

                      {/* Audio context fingerprint */}
                      {browserProfile.audioContext && (
                        <Card className="p-3 space-y-1">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Volume2 className="h-3 w-3" />Audio Engine</p>
                          <p className="text-lg font-bold text-foreground">{browserProfile.audioContext.sampleRate / 1000}kHz</p>
                          <p className="text-xs text-muted-foreground">{browserProfile.audioContext.channelCount}ch · {browserProfile.audioContext.state}</p>
                        </Card>
                      )}

                      {/* Canvas fingerprint */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Sparkles className="h-3 w-3" />Canvas FP</p>
                        <p className="text-sm font-bold text-foreground font-mono tracking-wide">
                          {browserProfile.canvasFingerprint ?? '—'}
                        </p>
                        <p className="text-[9px] text-muted-foreground">rendering signature hash</p>
                      </Card>

                      {/* Web Share / other APIs */}
                      <Card className="p-3 space-y-1">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Globe className="h-3 w-3" />APIs</p>
                        <p className="text-sm font-bold text-foreground">{browserProfile.webShareAvailable ? 'Web Share ✓' : 'No Web Share'}</p>
                        <p className="text-xs text-muted-foreground">
                          {browserProfile.touchPoints > 0 ? `${browserProfile.touchPoints} touch pts` : 'no touch'}
                        </p>
                      </Card>
                    </div>

                    {/* Media codecs */}
                    <div className="mt-3">
                      <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                        <Film className="h-3 w-3 text-violet-400" /> Media Codec Support
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {(Object.entries(browserProfile.mediaCodecs) as [keyof MediaCodecSupport, boolean][]).map(([codec, supported]) => (
                          <div key={codec} className={cn(
                            "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                            supported
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                              : "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20"
                          )}>
                            {supported ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                            {codec.toUpperCase()}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Capabilities row */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {[
                        { label: "Cookies", ok: browserProfile.cookiesEnabled },
                        { label: "localStorage", ok: browserProfile.localStorageAvailable },
                        { label: "sessionStorage", ok: browserProfile.sessionStorageAvailable },
                        { label: "IndexedDB", ok: browserProfile.indexedDBAvailable },
                        { label: "Service Worker", ok: browserProfile.serviceWorkerAvailable },
                        { label: "WebAssembly", ok: browserProfile.webAssemblyAvailable },
                        { label: "Geolocation", ok: browserProfile.geolocationAvailable },
                        { label: "Do Not Track", ok: browserProfile.doNotTrack === true, neutral: browserProfile.doNotTrack === null },
                      ].map(cap => (
                        <div key={cap.label} className={cn(
                          "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                          cap.neutral
                            ? "bg-muted/50 text-muted-foreground border-border"
                            : cap.ok
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                              : "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20"
                        )}>
                          {cap.neutral
                            ? <AlertCircle className="h-3 w-3" />
                            : cap.ok
                              ? <CheckCircle2 className="h-3 w-3" />
                              : <XCircle className="h-3 w-3" />
                          }
                          {cap.label}
                        </div>
                      ))}
                    </div>

                    {browserProfile.referrer && (
                      <p className="text-xs text-muted-foreground mt-2">
                        <span className="font-medium text-foreground">Referrer:</span> {browserProfile.referrer}
                      </p>
                    )}
                  </>
                ) : (
                  <Card className="p-6 text-center text-muted-foreground text-sm">Could not extract browser profile.</Card>
                )}
              </div>

              {/* ── COLLECTIVE VISITOR PATTERNS ── */}
              <div>
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                  <Globe className="h-4 w-4 text-purple-400" />
                  COLLECTIVE VISITOR PATTERNS
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">— all guests, all time, zero PII</span>
                </h3>

                {loadingVisitor ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                  </div>
                ) : guestAnalytics ? (
                  <>
                    {(() => {
                      const toggle = (key: string) => setActiveGuestStat(prev => prev === key ? null : key);

                      // ── helper: horizontal bar row ──
                      const GBarRow = ({ label, count, total, maxCount, color, medal }: { label: string; count: number; total: number; maxCount: number; color: string; medal?: string }) => (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-xs mb-0.5">
                              <span className="text-foreground font-medium truncate max-w-[65%]">{label}</span>
                              <span className="text-muted-foreground text-[10px]">{count} · {((count / total) * 100).toFixed(1)}%</span>
                            </div>
                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(count / maxCount) * 100}%`, background: color }} />
                            </div>
                          </div>
                          {medal && <span className="text-[9px] flex-shrink-0">{medal}</span>}
                        </div>
                      );

                      const PAGE_ICONS: Record<string, string> = {
                        home: '🏠', music: '🎵', artists: '🎤', marketplace: '🛒',
                        chat: '💬', profile: '👤', radio: '📻', events: '📅',
                        news: '📰', media: '📁', videos: '🎬', party: '🎉',
                        dashboard: '⚙️', playlists: '🎧', creator: '✨', help: '❓',
                        publishing: '📝', legal: '⚖️', signin: '🔐', onboarding: '🚀',
                      };

                      const SIZE_LABELS: Record<string, string> = {
                        xs_480: '< 480px  Phone S',
                        sm_480_767: '480–767px  Phone L',
                        md_768_1023: '768–1023px  Tablet',
                        lg_1024_1439: '1024–1439px  Laptop',
                        xl_1440plus: '1440px+  Desktop',
                      };

                      // Pre-compute top values for KPI cards
                      const topDevice = Object.entries(guestAnalytics.devices ?? {}).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
                      const topBrowser = Object.entries(guestAnalytics.browsers ?? {}).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
                      const topLanguage = Object.entries(guestAnalytics.languages ?? {}).sort((a, b) => b[1] - a[1])[0]?.[0]?.toUpperCase() ?? '—';
                      const topTimezone = (() => {
                        const entries = Object.entries(guestAnalytics.timezones ?? {}).sort((a, b) => b[1] - a[1]);
                        if (entries.length === 0) return '—';
                        return entries[0][0].replace(/_/g, ' ').split('/').pop() ?? '—';
                      })();
                      const peakHour = (() => {
                        const entries = Object.entries(guestAnalytics.hourOfDay ?? {}).sort((a, b) => b[1] - a[1]);
                        if (entries.length === 0) return '—';
                        const h = parseInt(entries[0][0]);
                        return `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`;
                      })();
                      const darkModePct = (() => {
                        const schemes = guestAnalytics.colorSchemes ?? {};
                        const total = Object.values(schemes).reduce((s, v) => s + v, 0);
                        if (total === 0) return '—';
                        return `${Math.round(((schemes.dark ?? 0) / total) * 100)}%`;
                      })();
                      const topOS = Object.entries(guestAnalytics.os ?? {}).sort((a, b) => b[1] - a[1])[0]?.[0]?.replace(/_/g, ' ') ?? '—';
                      const topReferrer = (() => {
                        const entries = Object.entries(guestAnalytics.referrers ?? {}).sort((a, b) => b[1] - a[1]);
                        if (entries.length === 0) return 'Direct';
                        return entries[0][0].replace(/_/g, '.').split('.')[0] ?? 'Direct';
                      })();

                      // ── drill-down panels ──
                      const guestDetailPanels: Record<string, React.ReactNode> = {

                        pageViews: (() => {
                          const entries = Object.entries(guestAnalytics.pages ?? {}).sort((a, b) => b[1] - a[1]) as [string, number][];
                          const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                          const maxCount = Math.max(1, entries[0]?.[1] ?? 1);
                          const medals = ['🥇','🥈','🥉'];
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <MousePointer className="h-3.5 w-3.5" /> Most Visited Pages
                                </h4>
                                {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-2">
                                    {entries.map(([page, count], i) => (
                                      <div key={page} className="flex items-center gap-2">
                                        <span className="text-base w-5 flex-shrink-0">{PAGE_ICONS[page] ?? '📄'}</span>
                                        <GBarRow label={`/${page}`} count={count} total={total} maxCount={maxCount} color={CHART_COLORS[i % CHART_COLORS.length]} medal={medals[i]} />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <BarChart3 className="h-3.5 w-3.5" /> Entry Traffic Split
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Each bar shows the share of all guest page views that landed on that section. Top entries signal strongest organic or direct traffic magnetism.
                                </p>
                                {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-1.5">
                                    {entries.slice(0, 8).map(([page, count], i) => {
                                      const pct = ((count / total) * 100).toFixed(1);
                                      return (
                                        <div key={page} className="flex items-center gap-2 text-xs">
                                          <span className="w-24 truncate text-muted-foreground">/{page}</span>
                                          <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden">
                                            <div className="h-full rounded-sm flex items-center pl-1.5 text-[9px] font-bold text-white" style={{ width: `${(count / maxCount) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }}>
                                              {parseFloat(pct) > 5 ? `${pct}%` : ''}
                                            </div>
                                          </div>
                                          <span className="text-muted-foreground w-10 text-right">{count}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </Card>
                            </div>
                          );
                        })(),

                        device: (() => {
                          const deviceEntries = Object.entries(guestAnalytics.devices ?? {}).sort((a, b) => b[1] - a[1]) as [string, number][];
                          const deviceTotal = deviceEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          const sizeEntries = Object.entries(guestAnalytics.screenSizes ?? {}).sort((a, b) => b[1] - a[1]) as [string, number][];
                          const sizeTotal = sizeEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          const dprEntries = Object.entries(guestAnalytics.pixelRatios ?? {}) as [string, number][];
                          const dprTotal = dprEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          const icons: Record<string, React.ReactNode> = {
                            mobile: <Smartphone className="h-5 w-5" />,
                            tablet: <Tablet className="h-5 w-5" />,
                            desktop: <Monitor className="h-5 w-5" />,
                          };
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Smartphone className="h-3.5 w-3.5" /> Device Class
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">Form factor split across all guest sessions. High mobile share signals the importance of mobile-first design.</p>
                                <div className="flex gap-3">
                                  {deviceEntries.map(([name, count], i) => (
                                    <div key={name} className="flex-1 text-center">
                                      <div className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center mb-2" style={{ background: CHART_COLORS[i % CHART_COLORS.length] + '22', color: CHART_COLORS[i % CHART_COLORS.length] }}>
                                        {icons[name] ?? <Monitor className="h-5 w-5" />}
                                      </div>
                                      <p className="text-xl font-bold">{((count / deviceTotal) * 100).toFixed(0)}%</p>
                                      <p className="text-xs text-muted-foreground capitalize">{name}</p>
                                      <p className="text-[10px] text-muted-foreground">{count} visits</p>
                                    </div>
                                  ))}
                                </div>
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Maximize2 className="h-3.5 w-3.5" /> Screen Size Buckets
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">Viewport width at time of visit. Informs responsive breakpoint priorities.</p>
                                {sizeEntries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-2">
                                    {sizeEntries.map(([bucket, count], i) => (
                                      <GBarRow key={bucket} label={SIZE_LABELS[bucket] ?? bucket.replace(/_/g, ' ')} count={count} total={sizeTotal} maxCount={sizeEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                  </div>
                                )}
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Sparkles className="h-3.5 w-3.5" /> Display Pixel Ratio
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">DPR indicates screen sharpness — 2× Retina, 3× Super-Hi. Impacts image asset resolution choices.</p>
                                <div className="flex gap-4 items-end mt-2">
                                  {(['1x','2x','3x'] as const).map((dpr, i) => {
                                    const count = (guestAnalytics.pixelRatios ?? {})[dpr] ?? 0;
                                    const pct = ((count / dprTotal) * 100).toFixed(0);
                                    return (
                                      <div key={dpr} className="flex-1 text-center">
                                        <div className="h-16 flex flex-col items-center justify-end mb-1">
                                          <div className="w-full rounded-t-lg" style={{ height: Math.max(8, (count / dprTotal) * 60) + 'px', background: CHART_COLORS[i] }} />
                                        </div>
                                        <p className="text-lg font-bold">{pct}%</p>
                                        <p className="text-xs text-muted-foreground">{dpr} DPR</p>
                                        <p className="text-[9px] text-muted-foreground">{dpr === '1x' ? 'Standard' : dpr === '2x' ? 'Retina' : 'Super-Hi'}</p>
                                        <p className="text-[9px] text-muted-foreground">{count} visits</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Card>
                            </div>
                          );
                        })(),

                        browser: (() => {
                          const browserEntries = Object.entries(guestAnalytics.browsers ?? {}).sort((a, b) => b[1] - a[1]) as [string, number][];
                          const browserTotal = browserEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          const versionEntries = Object.entries(guestAnalytics.browserVersions ?? {}).map(([k, v]) => [k.replace(/_/g, ' '), v] as [string, number]).sort((a, b) => b[1] - a[1]);
                          const versionTotal = versionEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          const osEntries = Object.entries(guestAnalytics.os ?? {}).map(([k, v]) => [k.replace(/_/g, ' '), v] as [string, number]).sort((a, b) => b[1] - a[1]);
                          const osTotal = osEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Globe className="h-3.5 w-3.5" /> Browser Share
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">Which browsers guests use to access the platform — critical for cross-browser compatibility prioritisation.</p>
                                {browserEntries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-2">
                                    {browserEntries.map(([name, count], i) => (
                                      <GBarRow key={name} label={name} count={count} total={browserTotal} maxCount={browserEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} medal={['🥇','🥈','🥉'][i]} />
                                    ))}
                                  </div>
                                )}
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Globe className="h-3.5 w-3.5" /> Browser Versions
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">Specific version distribution — reveals whether guests use up-to-date engines or legacy builds.</p>
                                {versionEntries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-2">
                                    {versionEntries.slice(0, 8).map(([name, count], i) => (
                                      <GBarRow key={name} label={name} count={count} total={versionTotal} maxCount={versionEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                  </div>
                                )}
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Monitor className="h-3.5 w-3.5" /> Operating System
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">OS mix across all guest sessions. iOS + Android dominance confirms mobile-first imperative.</p>
                                {osEntries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-2">
                                    {osEntries.slice(0, 7).map(([name, count], i) => (
                                      <GBarRow key={name} label={name} count={count} total={osTotal} maxCount={osEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} medal={['🥇','🥈','🥉'][i]} />
                                    ))}
                                  </div>
                                )}
                              </Card>
                            </div>
                          );
                        })(),

                        hours: (() => {
                          const maxHourCount = Math.max(1, ...Object.values(guestAnalytics.hourOfDay ?? {}));
                          const maxDayCount = Math.max(1, ...Object.values(guestAnalytics.dayOfWeek ?? {}));
                          const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Clock3 className="h-3.5 w-3.5" /> Activity by Hour of Day (UTC)
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Hour-by-hour traffic distribution for all guest visits. Peak bars reveal when your audience is most active — optimise publish timing accordingly.
                                </p>
                                <div className="flex gap-1 flex-wrap">
                                  {Array.from({ length: 24 }, (_, h) => {
                                    const count = guestAnalytics.hourOfDay?.[h.toString()] ?? 0;
                                    const intensity = count / maxHourCount;
                                    return (
                                      <div key={h} className="flex flex-col items-center gap-0.5" title={`${h}:00 UTC — ${count} views`}>
                                        <div className="w-[22px] rounded-sm transition-all" style={{ height: Math.max(6, intensity * 56) + 'px', background: `hsl(${250 - intensity * 60} 80% ${65 - intensity * 30}%)`, opacity: intensity < 0.05 ? 0.2 : 1 }} />
                                        <span className="text-[8px] text-muted-foreground">{h % 6 === 0 ? `${h}h` : ''}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                <p className="text-[9px] text-muted-foreground mt-2">Peak: {peakHour} UTC · Darker = more traffic</p>
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Calendar className="h-3.5 w-3.5" /> Activity by Day of Week
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Weekly cadence of guest sessions. Weekend vs weekday pattern reveals your audience's listening habits and helps time content drops.
                                </p>
                                {Object.keys(guestAnalytics.dayOfWeek ?? {}).length === 0 ? (
                                  <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p>
                                ) : (
                                  <>
                                    <div className="flex gap-2 items-end h-20 mb-1">
                                      {DAYS.map((day, i) => {
                                        const count = guestAnalytics.dayOfWeek?.[i.toString()] ?? 0;
                                        const pct = count / maxDayCount;
                                        return (
                                          <div key={day} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end">
                                            <span className="text-[9px] text-muted-foreground">{count}</span>
                                            <div className="w-full rounded-t-lg" style={{ height: Math.max(8, pct * 60) + 'px', background: `hsl(271 70% ${55 + (1 - pct) * 20}%)`, opacity: pct < 0.05 ? 0.3 : 1 }} />
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="flex gap-2">
                                      {DAYS.map(day => <div key={day} className="flex-1 text-center text-[9px] text-muted-foreground">{day}</div>)}
                                    </div>
                                  </>
                                )}
                              </Card>
                            </div>
                          );
                        })(),

                        sessions: (() => {
                          const refEntries = Object.entries(guestAnalytics.referrers ?? {}).map(([k, v]) => [k.replace(/_/g, '.'), v] as [string, number]).sort((a, b) => b[1] - a[1]);
                          const refTotal = refEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          const tzEntries = Object.entries(guestAnalytics.timezones ?? {}).sort((a, b) => b[1] - a[1]) as [string, number][];
                          const tzTotal = tzEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Map className="h-3.5 w-3.5" /> Traffic Sources
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Where guests came from before landing on BB. Direct traffic means typed URL or bookmark. Social links and search engines reveal external discovery channels.
                                </p>
                                {refEntries.length === 0 ? (
                                  <div className="text-center py-6">
                                    <p className="text-xs text-muted-foreground italic">All traffic so far is direct — no external referrers recorded yet.</p>
                                    <p className="text-[10px] text-muted-foreground/60 mt-1">External links, social shares, and search clicks will appear here as they accumulate.</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {refEntries.map(([name, count], i) => (
                                      <GBarRow key={name} label={name} count={count} total={refTotal} maxCount={refEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} medal={['🥇','🥈','🥉'][i]} />
                                    ))}
                                  </div>
                                )}
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Map className="h-3.5 w-3.5" /> Timezone Distribution
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Browser-reported timezone infers approximate geographic region without collecting any IP or location data. Informs scheduling for live events and drops.
                                </p>
                                {tzEntries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-2">
                                    {tzEntries.slice(0, 10).map(([tz, count], i) => (
                                      <GBarRow key={tz} label={tz.replace(/_/g, ' ')} count={count} total={tzTotal} maxCount={tzEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                  </div>
                                )}
                              </Card>
                            </div>
                          );
                        })(),

                        language: (() => {
                          const langEntries = Object.entries(guestAnalytics.languages ?? {}).sort((a, b) => b[1] - a[1]) as [string, number][];
                          const langTotal = langEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Languages className="h-3.5 w-3.5" /> Visitor Language Distribution
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Browser-reported UI language of all guest sessions. Directly informs which languages to prioritise for localisation, tooltips, and translated content.
                                </p>
                                {langEntries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-2">
                                    {langEntries.map(([lang, count], i) => (
                                      <GBarRow key={lang} label={lang.toUpperCase()} count={count} total={langTotal} maxCount={langEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} medal={['🥇','🥈','🥉'][i]} />
                                    ))}
                                  </div>
                                )}
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Languages className="h-3.5 w-3.5" /> Language × Sessions Analysis
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Language coverage as a share of all sessions. A single dominant language suggests a homogenous core audience. Multiple languages suggest international reach — consider multi-language support.
                                </p>
                                {langEntries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4 italic">Accumulating…</p> : (
                                  <div className="space-y-3">
                                    <div className="flex gap-1 flex-wrap">
                                      {langEntries.map(([lang, count], i) => {
                                        const pct = (count / langTotal) * 100;
                                        return (
                                          <div key={lang} className="flex flex-col items-center gap-0.5 min-w-[36px]" title={`${lang.toUpperCase()} — ${pct.toFixed(1)}%`}>
                                            <div className="w-8 rounded-sm" style={{ height: Math.max(8, (pct / 100) * 60) + 'px', background: CHART_COLORS[i % CHART_COLORS.length], opacity: 0.85 }} />
                                            <span className="text-[8px] text-muted-foreground">{lang.toUpperCase()}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <p className="text-[9px] text-muted-foreground">{langEntries.length} distinct language{langEntries.length !== 1 ? 's' : ''} detected across {langTotal} guest sessions</p>
                                  </div>
                                )}
                              </Card>
                            </div>
                          );
                        })(),

                        theme: (() => {
                          const schemeData = guestAnalytics.colorSchemes ?? {};
                          const schemeTotal = Object.values(schemeData).reduce((s, v) => s + v, 0) || 1;
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Moon className="h-3.5 w-3.5" /> Color Scheme Preference
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  OS-level dark/light mode preference reported by the guest's browser via <code className="text-[9px] bg-muted px-1 rounded">prefers-color-scheme</code>. Since BB defaults to dark, a high dark% is expected — but light% visitors deserve full contrast support.
                                </p>
                                <div className="flex gap-4">
                                  {([['dark', Moon, '#818cf8'], ['light', Sun, '#fbbf24'], ['no-preference', Globe, '#6b7280']] as const).map(([scheme, Icon, color]) => {
                                    const count = schemeData[scheme] ?? 0;
                                    const pct = ((count / schemeTotal) * 100).toFixed(0);
                                    return (
                                      <div key={scheme} className="flex-1 text-center">
                                        <div className="w-10 h-10 rounded-xl mx-auto flex items-center justify-center mb-2" style={{ background: color + '22', color }}>
                                          <Icon className="h-4 w-4" />
                                        </div>
                                        <p className="text-2xl font-bold">{pct}%</p>
                                        <p className="text-xs text-muted-foreground capitalize">{scheme.replace('-', ' ')}</p>
                                        <p className="text-[10px] text-muted-foreground">{count} visits</p>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="mt-4 h-2 rounded-full overflow-hidden flex">
                                  {([['dark', '#818cf8'], ['light', '#fbbf24'], ['no-preference', '#6b7280']] as const).map(([scheme, color]) => {
                                    const count = schemeData[scheme] ?? 0;
                                    const pct = (count / schemeTotal) * 100;
                                    if (pct < 1) return null;
                                    return <div key={scheme} style={{ width: `${pct}%`, background: color }} title={`${scheme}: ${pct.toFixed(1)}%`} />;
                                  })}
                                </div>
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Sparkles className="h-3.5 w-3.5" /> Display Pixel Ratio
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  DPR (device pixel ratio) indicates screen sharpness tier. 1× = standard LCD, 2× = Retina/AMOLED, 3× = flagship OLED. High 2–3× share means image assets should be served at ≥2× resolution for sharp rendering.
                                </p>
                                <div className="flex gap-4 items-end mt-2">
                                  {(['1x','2x','3x'] as const).map((dpr, i) => {
                                    const count = (guestAnalytics.pixelRatios ?? {})[dpr] ?? 0;
                                    const dprTotal = Object.values(guestAnalytics.pixelRatios ?? {}).reduce((s, v) => s + v, 0) || 1;
                                    const pct = ((count / dprTotal) * 100).toFixed(0);
                                    return (
                                      <div key={dpr} className="flex-1 text-center">
                                        <div className="h-20 flex flex-col items-center justify-end mb-1">
                                          <div className="w-full rounded-t-lg" style={{ height: Math.max(8, (count / dprTotal) * 70) + 'px', background: CHART_COLORS[i] }} />
                                        </div>
                                        <p className="text-xl font-bold">{pct}%</p>
                                        <p className="text-xs text-muted-foreground">{dpr} DPR</p>
                                        <p className="text-[9px] text-muted-foreground">{dpr === '1x' ? 'Standard LCD' : dpr === '2x' ? 'Retina/AMOLED' : 'Super-Hi OLED'}</p>
                                        <p className="text-[9px] text-muted-foreground">{count} visits</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Card>
                            </div>
                          );
                        })(),

                        sources: (() => {
                          const refEntries = Object.entries(guestAnalytics.referrers ?? {}).map(([k, v]) => [k.replace(/_/g, '.'), v] as [string, number]).sort((a, b) => b[1] - a[1]);
                          const refTotal = refEntries.reduce((s, [, v]) => s + v, 0) || 1;
                          const REFERRER_ICONS: Record<string, string> = {
                            google: '🔍', instagram: '📸', facebook: '📘', twitter: '🐦', x: '✖️',
                            youtube: '▶️', spotify: '🎧', tiktok: '🎵', reddit: '🟠', soundcloud: '☁️',
                            direct: '🔗', t: '✈️',
                          };
                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Map className="h-3.5 w-3.5" /> Top Referrers
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                                  Domains sending traffic to BB. Social media shares, search results, and press links all show up here. Empty = all traffic is direct (users typing the URL or using bookmarks).
                                </p>
                                {refEntries.length === 0 ? (
                                  <div className="text-center py-6">
                                    <p className="text-sm text-muted-foreground">All direct traffic so far</p>
                                    <p className="text-[10px] text-muted-foreground/60 mt-1">No external referrers recorded yet. Share links on socials to start seeing traffic sources here.</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {refEntries.map(([name, count], i) => {
                                      const domain = name.split('.')[0].toLowerCase();
                                      return (
                                        <div key={name} className="flex items-center gap-2">
                                          <span className="text-base w-5 flex-shrink-0">{REFERRER_ICONS[domain] ?? '🌐'}</span>
                                          <GBarRow label={name} count={count} total={refTotal} maxCount={refEntries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} medal={['🥇','🥈','🥉'][i]} />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </Card>
                              <Card className="p-4">
                                <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                  <Map className="h-3.5 w-3.5" /> Acquisition Strategy
                                </h4>
                                <p className="text-[10px] text-muted-foreground mb-4 leading-relaxed">
                                  Reading your referrer data: Social traffic = content is being shared. Search traffic = SEO is working. Direct traffic = brand recognition or QR scans. Each channel requires a different growth tactic.
                                </p>
                                <div className="space-y-3">
                                  {[
                                    { label: 'Social Media', keys: ['instagram','facebook','twitter','x','tiktok','reddit'], color: '#f472b6', icon: '📱' },
                                    { label: 'Search Engines', keys: ['google','bing','duckduckgo','yahoo'], color: '#60a5fa', icon: '🔍' },
                                    { label: 'Music Platforms', keys: ['spotify','soundcloud','youtube','bandcamp'], color: '#a78bfa', icon: '🎵' },
                                    { label: 'Direct / Bookmark', keys: [], color: '#34d399', icon: '🔗', isDirect: true },
                                  ].map(({ label, keys, color, icon, isDirect }) => {
                                    const count = isDirect
                                      ? Math.max(0, (guestAnalytics.totalSessions ?? 0) - refTotal)
                                      : refEntries.filter(([name]) => keys.some(k => name.toLowerCase().includes(k))).reduce((s, [, v]) => s + v, 0);
                                    const total = (guestAnalytics.totalSessions ?? 0) || 1;
                                    const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
                                    return (
                                      <div key={label} className="flex items-center gap-3">
                                        <span className="text-base w-5 flex-shrink-0">{icon}</span>
                                        <div className="flex-1">
                                          <div className="flex justify-between text-xs mb-0.5">
                                            <span className="text-foreground">{label}</span>
                                            <span className="text-muted-foreground">{pct}%</span>
                                          </div>
                                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Card>
                            </div>
                          );
                        })(),
                      };

                      // Drill-down renders DIRECTLY BELOW the row whose card is
                      // active — with it after both rows, tapping a row-1 card on
                      // a phone opened the panel off-screen and looked dead.
                      const ROW1_STATS = ['pageViews', 'device', 'browser', 'hours'];
                      const drillDownPanel = activeGuestStat && guestDetailPanels[activeGuestStat] ? (
                        <div className="mb-3 animate-in fade-in slide-in-from-top-2 duration-200">
                          {guestDetailPanels[activeGuestStat]}
                        </div>
                      ) : null;

                      return (
                        <>
                          {/* Row 1: 4 KPI cards */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            <StatCard icon={Eye} label="Guest Page Views" value={guestAnalytics.totalPageViews ?? 0} sub="all time aggregate" accent
                              onClick={() => toggle('pageViews')} active={activeGuestStat === 'pageViews'} />
                            <StatCard icon={Monitor} label="Top Device" value={topDevice} sub="most common form factor"
                              onClick={() => toggle('device')} active={activeGuestStat === 'device'} />
                            <StatCard icon={Globe} label="Top Browser" value={topBrowser} sub="engine split + OS"
                              onClick={() => toggle('browser')} active={activeGuestStat === 'browser'} />
                            <StatCard icon={Clock3} label="Peak Hour" value={peakHour} sub="busiest time UTC"
                              onClick={() => toggle('hours')} active={activeGuestStat === 'hours'} />
                          </div>

                          {/* Drill-down for row-1 cards — directly below their row */}
                          {ROW1_STATS.includes(activeGuestStat ?? '') && drillDownPanel}

                          {/* Row 2: 4 more KPI cards */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            <StatCard icon={Globe} label="Total Sessions" value={guestAnalytics.totalSessions ?? 0} sub="referrers + timezones"
                              onClick={() => toggle('sessions')} active={activeGuestStat === 'sessions'} />
                            <StatCard icon={Languages} label="Top Language" value={topLanguage} sub="browser locale distribution"
                              onClick={() => toggle('language')} active={activeGuestStat === 'language'} />
                            <StatCard icon={Moon} label="Dark Mode" value={darkModePct} sub="color scheme + display DPR"
                              onClick={() => toggle('theme')} active={activeGuestStat === 'theme'} />
                            <StatCard icon={Map} label="Top Source" value={topReferrer} sub="traffic acquisition channels"
                              onClick={() => toggle('sources')} active={activeGuestStat === 'sources'} />
                          </div>

                          {/* Drill-down for row-2 cards — directly below their row */}
                          {!ROW1_STATS.includes(activeGuestStat ?? '') && drillDownPanel}

                          {(guestAnalytics.totalPageViews ?? 0) === 0 && (
                            <Card className="p-6 text-center border-dashed mt-2">
                              <Globe className="h-6 w-6 mx-auto text-purple-400/30 mb-2" />
                              <p className="text-sm text-muted-foreground">Visitor pattern data is accumulating.</p>
                              <p className="text-xs text-muted-foreground/60 mt-1">Stats appear as guests browse the platform without signing in.</p>
                            </Card>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <Card className="p-8 text-center">
                    <Globe className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">No collective visitor data yet — data accumulates as guests visit.</p>
                  </Card>
                )}
              </div>

              {/* ── PLATFORM USER ACTIVITY ── */}
              <div>
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-emerald-400" />
                  PLATFORM USER ACTIVITY
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">— logged-in users, all time, zero PII</span>
                </h3>

                {loadingVisitor ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                  </div>
                ) : guestAnalytics ? (
                  <>
                    {/* KPI row — each card is clickable to expand details */}
                    {(() => {
                      const userData = (guestAnalytics.user as any) ?? {};
                      const userDevices = userData.devices ?? {};
                      const mobileCount = (userDevices.mobile ?? 0) + (userDevices.tablet ?? 0);
                      const totalDevices = Object.values(userDevices as Record<string, number>).reduce((s: number, v: number) => s + v, 0) || 1;
                      const mobilePct = Math.round((mobileCount / totalDevices) * 100);
                      const topBrowser = Object.entries(userData.browsers ?? {}).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] ?? "—";
                      const topDevice = Object.entries(userDevices).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] ?? "—";
                      const topMobileModel = Object.entries(userData.mobileModels ?? {}).sort((a: any, b: any) => b[1] - a[1])[0]?.[0]?.replace(/_/g, ' ') ?? "—";

                      const toggle = (key: string) => setActiveUserStat(prev => prev === key ? null : key);

                      // ── helper: horizontal bar row ──
                      const BarRow = ({ label, count, total, maxCount, color, medal }: { label: string; count: number; total: number; maxCount: number; color: string; medal?: string }) => (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-xs mb-0.5">
                              <span className="text-foreground font-medium truncate max-w-[65%]">{label}</span>
                              <span className="text-muted-foreground text-[10px]">{count} · {((count / total) * 100).toFixed(1)}%</span>
                            </div>
                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(count / maxCount) * 100}%`, background: color }} />
                            </div>
                          </div>
                          {medal && <span className="text-[9px] flex-shrink-0">{medal}</span>}
                        </div>
                      );

                      const SECTION_ICONS: Record<string, string> = {
                        home: '🏠', music: '🎵', artists: '🎤', marketplace: '🛒',
                        chat: '💬', profile: '👤', radio: '📻', events: '📅',
                        news: '📰', media: '📁', videos: '🎬', party: '🎉',
                        charts: '📊', dashboard: '⚙️', playlists: '🎧',
                        creator: '✨', help: '❓', publishing: '📝', legal: '⚖️',
                      };

                      const SIZE_LABELS: Record<string, string> = {
                        xs_480: '< 480px  Phone S',
                        sm_480_767: '480–767px  Phone L',
                        md_768_1023: '768–1023px  Tablet',
                        lg_1024_1439: '1024–1439px  Laptop',
                        xl_1440plus: '1440px+  Desktop',
                      };

                      // ── drill-down panels keyed by stat id ──
                      const detailPanels: Record<string, React.ReactNode> = {
                        pageViews: (() => {
                          const entries = Object.entries(userData.pages ?? {}).sort((a: any, b: any) => b[1] - a[1]) as [string, number][];
                          const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                          const maxCount = Math.max(1, entries[0]?.[1] ?? 1);
                          const medals = ['🥇','🥈','🥉'];
                          return (
                            <Card className="p-4">
                              <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                <MousePointer className="h-3.5 w-3.5" /> Most Visited Sections
                              </h4>
                              {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">Accumulating…</p> : (
                                <div className="space-y-2">
                                  {entries.map(([page, count], i) => (
                                    <div key={page} className="flex items-center gap-2">
                                      <span className="text-base w-5 flex-shrink-0">{SECTION_ICONS[page] ?? '📄'}</span>
                                      <BarRow label={`/${page}`} count={count} total={total} maxCount={maxCount} color={CHART_COLORS[i % CHART_COLORS.length]} medal={medals[i]} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </Card>
                          );
                        })(),

                        sessions: (() => {
                          const entries = Object.entries(userData.languages ?? {}).sort((a: any, b: any) => b[1] - a[1]) as [string, number][];
                          const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <Card className="p-4">
                              <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                <Languages className="h-3.5 w-3.5" /> Session Languages
                              </h4>
                              {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">Accumulating…</p> : (
                                <div className="space-y-2">
                                  {entries.slice(0, 10).map(([lang, count], i) => (
                                    <BarRow key={lang} label={lang.toUpperCase()} count={count} total={total} maxCount={entries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </div>
                              )}
                            </Card>
                          );
                        })(),

                        browser: (() => {
                          const entries = Object.entries(userData.browsers ?? {}).sort((a: any, b: any) => b[1] - a[1]) as [string, number][];
                          const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <Card className="p-4">
                              <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                <Globe className="h-3.5 w-3.5" /> All Browsers
                              </h4>
                              {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">Accumulating…</p> : (
                                <div className="space-y-2">
                                  {entries.map(([browser, count], i) => (
                                    <BarRow key={browser} label={browser} count={count} total={total} maxCount={entries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </div>
                              )}
                            </Card>
                          );
                        })(),

                        device: (() => {
                          const entries = Object.entries(userDevices).sort((a: any, b: any) => b[1] - a[1]) as [string, number][];
                          const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <Card className="p-4">
                              <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                <Monitor className="h-3.5 w-3.5" /> Device Types
                              </h4>
                              {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">Accumulating…</p> : (
                                <div className="space-y-2">
                                  {entries.map(([device, count], i) => (
                                    <BarRow key={device} label={device} count={count} total={total} maxCount={entries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </div>
                              )}
                            </Card>
                          );
                        })(),

                        mobile: (() => {
                          const entries = Object.entries(userData.screenSizes ?? {}).sort((a: any, b: any) => b[1] - a[1]) as [string, number][];
                          const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <Card className="p-4">
                              <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                <Maximize2 className="h-3.5 w-3.5" /> Screen Size Buckets
                              </h4>
                              {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">Accumulating…</p> : (
                                <div className="space-y-2">
                                  {entries.map(([bucket, count], i) => (
                                    <BarRow key={bucket} label={SIZE_LABELS[bucket] ?? bucket.replace(/_/g, ' ')} count={count} total={total} maxCount={entries[0][1]}
                                      color={bucket.startsWith('xs') || bucket.startsWith('sm') ? '#6366f1' : CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </div>
                              )}
                            </Card>
                          );
                        })(),

                        model: (() => {
                          const entries = Object.entries(userData.mobileModels ?? {}).sort((a: any, b: any) => b[1] - a[1]) as [string, number][];
                          const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
                          return (
                            <Card className="p-4">
                              <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                                <Smartphone className="h-3.5 w-3.5" /> Mobile Handsets
                              </h4>
                              {entries.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">Accumulating…</p> : (
                                <div className="space-y-2">
                                  {entries.map(([model, count], i) => (
                                    <BarRow key={model} label={model.replace(/_/g, ' ')} count={count} total={total} maxCount={entries[0][1]} color={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </div>
                              )}
                            </Card>
                          );
                        })(),
                      };

                      return (
                        <>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
                            <StatCard icon={Play}       label="Page Views"     value={guestAnalytics.userTotalPageViews ?? 0} sub="logged-in sessions"     accent
                              onClick={() => toggle('pageViews')} active={activeUserStat === 'pageViews'} />
                            <StatCard icon={Users}      label="Sessions"       value={guestAnalytics.userTotalSessions ?? 0}  sub="unique tab sessions"
                              onClick={() => toggle('sessions')} active={activeUserStat === 'sessions'} />
                            <StatCard icon={Globe}      label="Top Browser"    value={topBrowser}                              sub="logged-in visitors"
                              onClick={() => toggle('browser')} active={activeUserStat === 'browser'} />
                            <StatCard icon={Monitor}    label="Top Device"     value={topDevice}                               sub="most common type"
                              onClick={() => toggle('device')} active={activeUserStat === 'device'} />
                            <StatCard icon={Smartphone} label="Mobile Visits"  value={mobilePct > 0 ? `${mobilePct}%` : '—'}  sub={`${mobileCount} of ${totalDevices}`}
                              onClick={() => toggle('mobile')} active={activeUserStat === 'mobile'} />
                            <StatCard icon={Cpu}        label="Top Handset"    value={topMobileModel !== '—' ? topMobileModel.split('/')[0].trim() : '—'} sub="most common model"
                              onClick={() => toggle('model')} active={activeUserStat === 'model'} />
                          </div>

                          {/* Drill-down panel — slides in below the grid */}
                          {activeUserStat && detailPanels[activeUserStat] && (
                            <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                              {detailPanels[activeUserStat]}
                            </div>
                          )}

                          {(guestAnalytics.userTotalPageViews ?? 0) === 0 && (
                            <Card className="p-6 text-center border-dashed mt-2">
                              <Activity className="h-6 w-6 mx-auto text-emerald-400/30 mb-2" />
                              <p className="text-sm text-muted-foreground">User activity data is now being collected.</p>
                              <p className="text-xs text-muted-foreground/60 mt-1">Stats will appear as logged-in users navigate the platform.</p>
                            </Card>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <Card className="p-8 text-center border-dashed">
                    <Activity className="h-8 w-8 mx-auto text-emerald-400/30 mb-3" />
                    <p className="text-sm text-muted-foreground">User activity data is now being collected.</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Stats will appear as logged-in users navigate the platform.</p>
                  </Card>
                )}
              </div>

              {/* ── HOW THE WEB WATCHES YOU ── */}
              <div>
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
                  <BookOpen className="h-4 w-4 text-amber-400" />
                  HOW THE WEB WATCHES YOU
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">— the deep nerdy explainer</span>
                </h3>
                <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                  Every browser is a leaky vessel of signals. No login required, no consent needed — here is a field guide to every technique being used on you right now, with the technical precision that most privacy articles lack.
                </p>

                {/* Cookies & Storage */}
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Lock className="h-3 w-3 text-blue-400" /> Storage Vectors
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                  {[
                    {
                      icon: Lock,
                      color: "text-blue-400",
                      bg: "bg-blue-500/10 border-blue-500/20",
                      title: "Session vs. Persistent Cookies",
                      body: `Session cookies live in memory and evaporate when the tab closes — like DRAM. Persistent cookies are written to disk with a Max-Age or Expires header set by the server. Most ad networks write 90-day+ cookies on first load. The Set-Cookie SameSite=None; Secure combination is what allows cross-site identity stitching — every time you visit a site with a Facebook Like button, Facebook's cookie is refreshed, updating your timestamp in their graph.`,
                    },
                    {
                      icon: Database,
                      color: "text-rose-400",
                      bg: "bg-rose-500/10 border-rose-500/20",
                      title: "localStorage / IndexedDB — The Evercookie",
                      body: `Researcher Samy Kamkar's evercookie (2010) stored a unique ID across 13 different storage mechanisms simultaneously: cookies, localStorage, sessionStorage, IndexedDB, userData (IE), Web SQL, PNG pixel caching in canvas, ETags, window.name, HTTP cache, CSS :visited history, and AudioContext state. Clear one — the ID is resurrected from the others. Modern browsers partitioned third-party storage to counter this, but same-origin evercookies still work.`,
                    },
                    {
                      icon: Server,
                      color: "text-emerald-400",
                      bg: "bg-emerald-500/10 border-emerald-500/20",
                      title: "CNAME Cloaking — First-Party Trackers in Disguise",
                      body: `Third-party cookie restrictions only block cookies from different eTLD+1 domains. Clever ad networks started using CNAME records to create subdomains that resolve to tracker infrastructure: analytics.yoursite.com → trackernetwork.io. The browser sees a same-site request — restrictions don't apply. Safari's ITP now detects CNAME cloaking via TTL analysis. Firefox blocks known CNAME trackers via uBO. Chrome does not.`,
                    },
                  ].map(card => (
                    <Card key={card.title} className={cn("p-4 border", card.bg)}>
                      <div className="flex items-start gap-2 mb-2">
                        <card.icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", card.color)} />
                        <h4 className="text-xs font-semibold text-foreground">{card.title}</h4>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{card.body}</p>
                    </Card>
                  ))}
                </div>

                {/* Fingerprinting */}
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Fingerprint className="h-3 w-3 text-purple-400" /> Fingerprinting Techniques
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                  {[
                    {
                      icon: Fingerprint,
                      color: "text-purple-400",
                      bg: "bg-purple-500/10 border-purple-500/20",
                      title: "Canvas Fingerprinting — Pixel-Level Uniqueness",
                      body: `A hidden <canvas> element draws a string of text using specific font, size, and color, then overlays shapes and gradients. ctx.getImageData() reads back the pixel RGBA values as a Uint8ClampedArray. Differences in GPU rasterisation, subpixel antialiasing, and installed fonts produce subtly different pixel values per device. The SHA-256 of that array is your canvas fingerprint. EFF's Panopticlick found it provides ~10 bits of entropy — uniquely identifying ~1 in 1,024 browsers.`,
                    },
                    {
                      icon: Cpu,
                      color: "text-amber-400",
                      bg: "bg-amber-500/10 border-amber-500/20",
                      title: "AudioContext Fingerprinting",
                      body: `The Web Audio API creates an OscillatorNode (triangle wave, 10kHz), routes it through a DynamicsCompressorNode and AnalyserNode, and reads float32 sample values from the buffer. CPU floating-point unit differences and audio driver quirks produce slightly different values across hardware. The sum of the first 5,000 sample values gives ~8 bits of entropy. This technique was added to most fingerprinting libraries around 2016 precisely because it's invisible, requires no permissions, and works even with WebGL blocked.`,
                    },
                    {
                      icon: Monitor,
                      color: "text-cyan-400",
                      bg: "bg-cyan-500/10 border-cyan-500/20",
                      title: "Font Enumeration via CSS Measurement",
                      body: `A probe div is rendered with a generic fallback font (monospace, sans-serif), its offsetWidth measured. The font-family is then swapped to each of ~700 test font names. If the width changes, the font is installed. No file system access, no permissions — pure DOM measurement. Installed fonts leak OS version, language pack, and software (e.g., Adobe fonts signal Creative Cloud). Combined across 700 probes, font lists contribute ~13 bits of entropy — the highest entropy fingerprinting signal in most browser profiles.`,
                    },
                    {
                      icon: Cpu,
                      color: "text-indigo-400",
                      bg: "bg-indigo-500/10 border-indigo-500/20",
                      title: "WebGL Deep Fingerprinting",
                      body: `Beyond RENDERER and VENDOR strings (shown in your dossier), the WebGL context exposes: MAX_TEXTURE_SIZE, MAX_VIEWPORT_DIMS, MAX_VERTEX_ATTRIBS, supported extensions list, and GLSL shader precision formats. Each GPU model has a unique capability profile. Running a WebGL benchmark renders a 3D scene and measures pixel output from a fragment shader — differences in driver-level floating point reveal exact GPU revision. WebGL fingerprinting contributes ~10 bits of entropy and cannot be spoofed without breaking WebGL functionality entirely.`,
                    },
                    {
                      icon: HardDrive,
                      color: "text-rose-400",
                      bg: "bg-rose-500/10 border-rose-500/20",
                      title: "Battery API as Covert Tracking Channel",
                      body: `The Battery Status API (W3C spec, 2015) exposed level (0.0–1.0 in 1% steps), charging (boolean), chargingTime, and dischargingTime. Researchers showed the combination of level + chargingTime forms a ~32-bit identifier that persists across tabs, private windows, and cookie clears. A tracker could match anonymous and logged-in sessions by battery signature. Mozilla removed it from Firefox 52 (2017). Chrome never implemented it for Android. The W3C deprecated the spec entirely. It remains in Safari on macOS with reduced precision.`,
                    },
                    {
                      icon: Eye,
                      color: "text-emerald-400",
                      bg: "bg-emerald-500/10 border-emerald-500/20",
                      title: "CSS Media Query & Sensor Fingerprinting",
                      body: `window.matchMedia() probes device characteristics without any API permissions: (prefers-color-scheme: dark) reveals OS theme, (pointer: coarse) reveals touchscreen, (hover: none) reveals mobile, (update: slow) reveals e-ink display, (forced-colors: active) reveals accessibility mode. Combined with screen.orientation, devicePixelRatio, and innerWidth, these queries produce a device class fingerprint. The -webkit-device-pixel-ratio media query also exposes fractional DPR on certain Android devices, pinpointing exact phone model families.`,
                    },
                  ].map(card => (
                    <Card key={card.title} className={cn("p-4 border", card.bg)}>
                      <div className="flex items-start gap-2 mb-2">
                        <card.icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", card.color)} />
                        <h4 className="text-xs font-semibold text-foreground">{card.title}</h4>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{card.body}</p>
                    </Card>
                  ))}
                </div>

                {/* Network & Protocol */}
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Wifi className="h-3 w-3 text-cyan-400" /> Network &amp; Protocol Layer
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                  {[
                    {
                      icon: Wifi,
                      color: "text-cyan-400",
                      bg: "bg-cyan-500/10 border-cyan-500/20",
                      title: "TLS / JA3 Fingerprinting",
                      body: `Every TLS handshake starts with a ClientHello packet that lists: TLS version, cipher suite order, extension list, elliptic curve groups, and EC point formats. The JA3 algorithm (Salesforce, 2017) hashes these fields into a 32-character MD5 string — a browser fingerprint at the network layer, invisible to the browser itself. JA4 (2023 successor) adds sorting and deduplication for robustness. Web servers and CDNs log JA3/JA4 hashes per request — even a VPN can't hide it, since the fingerprint is in the TLS handshake, not the HTTP headers.`,
                    },
                    {
                      icon: Wifi,
                      color: "text-blue-400",
                      bg: "bg-blue-500/10 border-blue-500/20",
                      title: "HTTP/2 HPACK Fingerprinting",
                      body: `HTTP/2 sends request headers in a specific order dictated by the browser's HTTP/2 stack implementation. Chrome sends :method, :authority, :scheme, :path in a distinct order with specific pseudo-header placements. Firefox's order differs. Safari's differs further. Combined with HPACK static table references and Huffman encoding preferences, the header order provides ~8 bits of entropy, uniquely identifying browser engine and version. Cloudflare's ML-based bot detection uses this signal.`,
                    },
                    {
                      icon: Activity,
                      color: "text-rose-400",
                      bg: "bg-rose-500/10 border-rose-500/20",
                      title: "Cache Timing Side-Channel",
                      body: `Pre-2020, CSS :visited allowed tracking browsing history: if(getComputedStyle(link).color === 'purple') { siteVisited = true }. Patched via computed style restrictions. Modern variant: cache timing. Fetch a resource from a known site, measure response time. If under ~20ms, it's in the HTTP cache — you've visited recently. Chrome's Cache Partitioning (2020) isolated caches per top-level origin+frame origin, breaking this. But DNS cache timing and connection reuse timing still leak partial history in some browsers.`,
                    },
                  ].map(card => (
                    <Card key={card.title} className={cn("p-4 border", card.bg)}>
                      <div className="flex items-start gap-2 mb-2">
                        <card.icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", card.color)} />
                        <h4 className="text-xs font-semibold text-foreground">{card.title}</h4>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{card.body}</p>
                    </Card>
                  ))}
                </div>

                {/* Entropy Math */}
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <TerminalSquare className="h-3 w-3 text-emerald-400" /> The Entropy Mathematics
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                  {[
                    {
                      icon: TerminalSquare,
                      color: "text-emerald-400",
                      bg: "bg-emerald-500/10 border-emerald-500/20",
                      title: "How Bits of Entropy Work",
                      body: `Entropy measures how much a signal narrows down a population. 1 bit halves it — if 50% of browsers have a given trait, knowing you have it gives 1 bit. Formula: H = -log₂(p) where p = fraction of browsers sharing this value. A screen resolution of 1920×1080 might appear in 22% of browsers → H = -log₂(0.22) ≈ 2.2 bits. The key insight: signals are independent, so bits add. The EFF's Panopticlick database showed that 8–10 signals (screen res + timezone + UA + fonts + plugins + canvas + WebGL + languages) provide 19–23 bits total — enough to uniquely identify 1 in ~8 million browsers.`,
                    },
                    {
                      icon: Shield,
                      color: "text-amber-400",
                      bg: "bg-amber-500/10 border-amber-500/20",
                      title: "The Privacy Paradox: Uncommon = Less Private",
                      body: `Privacy is safety in crowds. The most trackable browser is the most unusual one. Running Linux with a rare locale, a custom screen resolution, no plugins, and Tor Browser with JavaScript enabled (to avoid broken sites) makes you highly unique — exactly what fingerprinters want. Tor Browser deliberately makes all users look identical by spoofing common Windows metrics. The goal isn't to hide — it's to blend. A boring, mainstream browser profile on a mainstream OS is genuinely harder to track than an exotic privacy-hardened setup.`,
                    },
                    {
                      icon: Server,
                      color: "text-indigo-400",
                      bg: "bg-indigo-500/10 border-indigo-500/20",
                      title: "What We Actually Collect — Zero PII, by Design",
                      body: `Our guest tracker in guest-analytics.ts increments a single shared Firestore document with atomic counters: page path bucket, browser family, device class (mobile/tablet/desktop), OS family, hour of day (UTC), day of week, screen size bucket (xs/sm/md/lg/xl). There is no user ID, no IP address, no cookie, no session identifier, no fingerprint hash, no timestamp per-visit. The data is structurally incapable of identifying individuals — it is pure population-level signal, equivalent to a turnstile counter.`,
                    },
                    {
                      icon: Shield,
                      color: "text-purple-400",
                      bg: "bg-purple-500/10 border-purple-500/20",
                      title: "How to Actually Disappear",
                      body: `Tier 1 (most users): Firefox + uBlock Origin (hard mode) + Total Cookie Protection. Blocks 95% of trackers. Tier 2 (privacy-aware): Brave Browser — built-in shield blocks canvas/audio/font fingerprinting by injecting noise. Tier 3 (high-risk individuals): Tor Browser — identical fingerprint for all users, Tor exit nodes mask IP. VPNs only mask IP (Layer 3) — fingerprinting operates at Layer 7 (application). A VPN + Chrome is much weaker than Firefox + uBlock. The single highest-impact move: block third-party JavaScript from unknown origins.`,
                    },
                  ].map(card => (
                    <Card key={card.title} className={cn("p-4 border", card.bg)}>
                      <div className="flex items-start gap-2 mb-2">
                        <card.icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", card.color)} />
                        <h4 className="text-xs font-semibold text-foreground">{card.title}</h4>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{card.body}</p>
                    </Card>
                  ))}
                </div>

                {/* Adaptive Loading - keep original */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {
                      icon: Wifi,
                      color: "text-cyan-400",
                      bg: "bg-cyan-500/10 border-cyan-500/20",
                      title: "Network API & Adaptive Loading",
                      body: "navigator.connection exposes your effective connection type (2G/3G/4G/wifi), estimated downlink Mbps, and RTT latency estimate in milliseconds. Responsible apps use this to serve lighter assets on slow connections — lower-bitrate audio on 2G, skip video preloading on metered connections. The saveData flag signals the user has enabled data saver mode. Your connection type is shown in your dossier above. This API is in Chrome/Android only — Firefox and Safari intentionally withheld it as a fingerprinting vector.",
                    },
                    {
                      icon: Eye,
                      color: "text-rose-400",
                      bg: "bg-rose-500/10 border-rose-500/20",
                      title: "IntersectionObserver Ping Tracking",
                      body: "Ad measurement requires knowing when an ad enters the user's viewport. The IntersectionObserver API fires a callback when a target element becomes X% visible. A 1×1 transparent pixel with an IntersectionObserver sends a beacon to the ad server the moment it enters view — confirming 'viewable impression' under MRC standards (50% of pixels visible for ≥1 second). This is how display ad CPM billing verification works. No JavaScript click handler required — just the element existing in DOM and scrolling into view is enough to generate a billable event.",
                    },
                  ].map(card => (
                    <Card key={card.title} className={cn("p-4 border", card.bg)}>
                      <div className="flex items-start gap-2 mb-2">
                        <card.icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", card.color)} />
                        <h4 className="text-xs font-semibold text-foreground">{card.title}</h4>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{card.body}</p>
                    </Card>
                  ))}
                </div>
              </div>

            </div>
          </TabsContent>

          {/* ══════════════════════════════════════════════════════════════
              DEV SESSIONS TAB (admin/superadmin only)
          ══════════════════════════════════════════════════════════════ */}
          {isAdmin && (
            <TabsContent value="dev">
              <div className="space-y-6">
                {/* Summary stats */}
                {loadingDev ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                  </div>
                ) : devSummary ? (
                  <>
                    {/* Rounded numbers; a card slides its details open (Gordon 2026-11-02). */}
                    <DevGlance glance={devSummary.glance} />

                    {/* Monthly cost chart */}
                    {devSummary.byMonth.length > 0 && (
                      <Card className="p-4">
                        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                          <Activity className="h-4 w-4 text-muted-foreground" /> Development Cost by Month
                        </h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={devSummary.byMonth} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                            <Tooltip
                              contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                              formatter={(val: number, name: string) => [name === "cost" ? fmtSek(val) : `${Math.round(val)} h`, name === "cost" ? "Cost" : "Hours"]}
                            />
                            <Legend />
                            <Bar dataKey="hours" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} name="hours" />
                            <Bar dataKey="cost"  fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} name="cost" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    )}

                    {/* Recent sessions */}
                    <div>
                      <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-3">
                        <Code2 className="h-4 w-4 text-muted-foreground" /> Recent Sessions
                      </h2>
                      <div className="space-y-2">
                        {devSummary.recent.length === 0 ? (
                          <Card className="p-8 text-center">
                            <Code2 className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                            <p className="text-sm text-muted-foreground">No dev sessions logged yet</p>
                            <p className="text-xs text-muted-foreground mt-1">Sessions are recorded automatically when admins use the platform</p>
                          </Card>
                        ) : devSummary.recent.map(session => {
                          const startDate = session.startTime?.toDate?.() || new Date();
                          const typeColors: Record<string, string> = {
                            ai: "bg-purple-500/20 text-purple-400 border-purple-500/30",
                            manual: "bg-blue-500/20 text-blue-400 border-blue-500/30",
                            review: "bg-amber-500/20 text-amber-400 border-amber-500/30",
                          };
                          return (
                            <Card key={session.id} className="p-4 flex items-start gap-4">
                              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                                {session.type === "ai" ? <Zap className="h-5 w-5 text-purple-400" /> :
                                 session.type === "review" ? <Activity className="h-5 w-5 text-amber-400" /> :
                                 <Code2 className="h-5 w-5 text-blue-400" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="text-sm font-semibold text-foreground">
                                    {session.description || `${session.type} session`}
                                  </span>
                                  <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded-full border capitalize", typeColors[session.type] || typeColors.manual)}>
                                    {session.type}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDuration(session.activeDurationSeconds)}</span>
                                  <span className="flex items-center gap-1"><Euro className="h-3 w-3" /> {fmtSek(devCostSek(session.activeDurationSeconds || 0))}</span>
                                  {session.keystrokesTotal > 0 && <span className="flex items-center gap-1"><Keyboard className="h-3 w-3" /> {fmt(session.keystrokesTotal)} keys</span>}
                                  {session.mouseEventsTotal > 0 && <span className="flex items-center gap-1"><MousePointer2 className="h-3 w-3" /> {fmt(session.mouseEventsTotal)} events</span>}
                                  <span className="ml-auto">{startDate.toLocaleDateString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">Failed to load dev sessions</div>
                )}
              </div>
            </TabsContent>
          )}

          {/* ══════════════════════════════════════════════════════════════
              FINANCE TAB
          ══════════════════════════════════════════════════════════════ */}
          {user && (
            <TabsContent value="finance">
              <FinanceTab
                revenue={financeRevenue}
                expenses={financeExpenses}
                loading={loadingFinance}
                failed={financeError}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

// ─── Finance Tab Component ────────────────────────────────────────────────────

function FinanceTab({
  revenue: allRevenue,
  expenses: allExpenses,
  loading,
  failed = false,
}: {
  revenue: CreatorRevenue[];
  expenses: CreatorExpense[];
  loading: boolean;
  failed?: boolean;
}) {
  const tCommon = useTranslations("common");
  const tMarketplace = useTranslations('marketplace');
  const tNav = useTranslations("nav");
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const unit = (entry: { currency?: string }) => (entry.currency || 'EUR').toUpperCase();
  const currencies = [...new Set([...allRevenue, ...allExpenses].map(unit))].sort();
  const currency = currencies.includes(selectedCurrency) ? selectedCurrency : currencies[0] || 'EUR';
  const revenue = allRevenue.filter(entry => unit(entry) === currency).map(entry => ({ ...entry, amount: Number(entry.amount) || 0 }));
  const expenses = allExpenses.filter(entry => unit(entry) === currency).map(entry => ({ ...entry, amount: Number(entry.amount) || 0 }));
  const fmtMoney = (amount: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);

  // ── Derived totals ──
  const totalRevenue  = revenue.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const netProfit     = totalRevenue - totalExpenses;
  const margin        = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  // ── Monthly aggregates (last 12 months) ──
  const monthlyData = (() => {
    const map: Record<string, { month: string; revenue: number; expenses: number }> = {};
    for (let i = 11; i >= 0; i--) {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString("en", { month: "short", year: "2-digit" });
      map[key] = { month: label, revenue: 0, expenses: 0 };
    }
    revenue.forEach(r => {
      const key = (r.date || "").slice(0, 7);
      if (map[key]) map[key].revenue += r.amount || 0;
    });
    expenses.forEach(e => {
      const key = (e.date || "").slice(0, 7);
      if (map[key]) map[key].expenses += e.amount || 0;
    });
    return Object.values(map);
  })();

  // ── Revenue by source (pie) ──
  const revenueBySource = (() => {
    const map: Record<string, number> = {};
    revenue.forEach(r => {
      const src = r.source || "Other";
      map[src] = (map[src] || 0) + (Number(r.amount) || 0);
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
  })();

  // ── Expenses by category (horizontal bar) ──
  const expensesByCategory = (() => {
    const map: Record<string, number> = {};
    expenses.forEach(e => {
      const cat = e.category || "Other";
      map[cat] = (map[cat] || 0) + (Number(e.amount) || 0);
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
  })();

  // ── Recent transactions (merged, sorted newest first) ──
  const recentTxns = [
    ...revenue.map(r => ({ ...r, _type: "revenue" as const })),
    ...expenses.map(e => ({ ...e, _type: "expense" as const })),
  ]
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
    .slice(0, 12);

  const tooltipStyle = {
    contentStyle: {
      background: "hsl(var(--background))",
      border: "1px solid hsl(var(--border))",
      borderRadius: 8,
      fontSize: 12,
    },
  };

  const isEmpty = revenue.length === 0 && expenses.length === 0;

  if (failed) return <Card role="alert" className="p-6">{tCommon("error")}</Card>;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-72" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        {/* The numbers come from the person's books — add receipts and income there. */}
        <Link href="/bookkeeping" className="mr-auto text-sm font-semibold underline underline-offset-2">{tNav("bookkeeping")} →</Link>
        <label className="flex items-center gap-2 text-sm">{tMarketplace('currencyLabel')}<select value={currency} onChange={event => setSelectedCurrency(event.target.value)} className="h-9 border border-border bg-background px-2" aria-label={tMarketplace('currencyLabel')}>{(currencies.length ? currencies : ['EUR']).map(unit => <option key={unit} value={unit}>{unit}</option>)}</select></label></div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 flex items-start gap-3 border-green-500/30 bg-green-500/5">
          <div className="w-10 h-10 rounded-xl bg-green-500/15 flex items-center justify-center flex-shrink-0">
            <Wallet className="h-5 w-5 text-green-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Total Revenue</p>
            <p className="text-2xl font-bold text-foreground leading-tight">{fmtMoney(totalRevenue)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{revenue.length} entries</p>
          </div>
        </Card>

        <Card className="p-4 flex items-start gap-3 border-red-500/30 bg-red-500/5">
          <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
            <Receipt className="h-5 w-5 text-red-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Total Expenses</p>
            <p className="text-2xl font-bold text-foreground leading-tight">{fmtMoney(totalExpenses)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{expenses.length} entries</p>
          </div>
        </Card>

        <Card className={cn("p-4 flex items-start gap-3", netProfit >= 0 ? "border-emerald-500/30 bg-emerald-500/5" : "border-orange-500/30 bg-orange-500/5")}>
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0", netProfit >= 0 ? "bg-emerald-500/15" : "bg-orange-500/15")}>
            {netProfit >= 0
              ? <TrendingUp className="h-5 w-5 text-emerald-500" />
              : <TrendingDown className="h-5 w-5 text-orange-400" />}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Net Profit</p>
            <p className={cn("text-2xl font-bold leading-tight", netProfit >= 0 ? "text-emerald-500" : "text-orange-400")}>{fmtMoney(netProfit)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{netProfit >= 0 ? "in the black" : "in the red"}</p>
          </div>
        </Card>

        <Card className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
            <Percent className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Profit Margin</p>
            <p className="text-2xl font-bold text-foreground leading-tight">{totalRevenue > 0 ? margin.toFixed(1) + "%" : "—"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">revenue kept</p>
          </div>
        </Card>
      </div>

      {isEmpty && (
        <Card className="p-12 text-center">
          <PiggyBank className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm font-semibold text-foreground mb-1">No financial data yet</p>
          <p className="text-xs text-muted-foreground">Revenue and expense entries will appear here once recorded.</p>
        </Card>
      )}

      {/* ── Graph 1: Revenue vs Expenses over time ── */}
      {!isEmpty && (
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" /> Revenue vs Expenses — Last 12 Months
          </h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlyData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => fmtMoney(v)} />
              <Tooltip
                {...tooltipStyle}
                formatter={(value: number, name: string) => [fmtMoney(value), name === "revenue" ? "Revenue" : "Expenses"]}
              />
              <Legend formatter={v => v === "revenue" ? "Revenue" : "Expenses"} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue"  fill="#22c55e" radius={[4, 4, 0, 0]} name="revenue" />
              <Bar dataKey="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} name="expenses" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Graph 2 + 3: pie + horizontal bar ── */}
      {!isEmpty && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          {/* Revenue by Source */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <ArrowUpRight className="h-4 w-4 text-green-500" /> Revenue by Source
            </h2>
            {revenueBySource.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-10">No revenue entries</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={revenueBySource}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                      paddingAngle={2}
                    >
                      {revenueBySource.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      {...tooltipStyle}
                      formatter={(value: number, name: string) => [fmtMoney(value), name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 space-y-1">
                  {revenueBySource.map((src, i) => (
                    <div key={src.name} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="text-muted-foreground truncate">{src.name}</span>
                      </span>
                      <span className="font-medium text-foreground ml-2">{fmtMoney(src.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          {/* Expenses by Category */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <ArrowDownRight className="h-4 w-4 text-red-400" /> Expenses by Category
            </h2>
            {expensesByCategory.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-10">No expense entries</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={expensesByCategory}
                  layout="vertical"
                  margin={{ left: 16, right: 16, top: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => fmtMoney(v)} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={110} />
                  <Tooltip
                    {...tooltipStyle}
                    formatter={(value: number) => [fmtMoney(value), "Spent"]}
                  />
                  <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {/* ── Recent Transactions ── */}
      {!isEmpty && recentTxns.length > 0 && (
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-muted-foreground" /> Recent Transactions
          </h2>
          <div className="space-y-2">
            {recentTxns.map(txn => {
              const isRev = txn._type === "revenue";
              const rev = txn as CreatorRevenue & { _type: "revenue" };
              const exp = txn as CreatorExpense  & { _type: "expense" };
              return (
                <Card key={txn.id + txn._type} className="p-3 flex items-center gap-3">
                  <div className={cn(
                    "w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0",
                    isRev ? "bg-green-500/15" : "bg-red-500/15"
                  )}>
                    {isRev
                      ? <ArrowUpRight className="h-4 w-4 text-green-500" />
                      : <ArrowDownRight className="h-4 w-4 text-red-400" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {isRev ? rev.source : exp.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isRev ? "Revenue" : exp.category} · {txn.date}
                    </p>
                  </div>
                  <span className={cn("text-sm font-bold flex-shrink-0", isRev ? "text-green-500" : "text-red-400")}>
                    {isRev ? "+" : "−"}{fmtMoney(txn.amount || 0)}
                  </span>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
